import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AngleUpComponent } from '../../icons/angle-up';
import { AngleDownComponent } from '../../icons/angle-down';
import { EditComponent } from '../../icons/edit';
import { LocaleService } from 'src/app/services/locale';
import { UserService } from 'src/app/services/user';
import { LocodeToCountryNamePipe } from '../../../pipes/country-name.pipe';
const CATEGORY_TOTALS_KEY = 'calculation_category_totals';

export interface EmissionTotals {
  co2: number;
  ch4: number;
  n2o: number;
  co2e: number;
}

@Component({
  imports: [
    CommonModule,
    RouterModule,
    AngleUpComponent,
    AngleDownComponent,
    EditComponent,
    LocodeToCountryNamePipe,
  ],
  selector: 'app-calculation-summary',
  styles: [
    `
      .summary-container {
        background: linear-gradient(135deg, #ffffff 0%, #f8faf9 100%);
      }
      .category-header {
        background: linear-gradient(90deg, #043b22 0%, #065a35 100%);
        transition: all 0.3s ease;
      }
      /*
    .category-header:hover {
      background: linear-gradient(90deg, #065a35 0%, #043B22 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(4, 59, 34, 0.15);
    }
    */
      .category-content {
        animation: slideDown 0.3s ease-out;
      }
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .question-row {
        transition: all 0.2s ease;
        border-left: 3px solid transparent;
      }
      .question-row:hover {
        background: #f8faf9;
        border-left-color: #043b22;
      }
      .data-card {
        background: linear-gradient(135deg, #ffffff 0%, #f8faf9 100%);
        border: 1px solid #e5e7eb;
        transition: all 0.2s ease;
      }
      .data-card:hover {
        border-color: #043b22;
        box-shadow: 0 2px 8px rgba(4, 59, 34, 0.1);
      }
      .edit-btn {
        transition: all 0.2s ease;
      }
      /*
    .edit-btn:hover {
      transform: scale(1.05);
    }
      */
      .emissions-table th {
        background: linear-gradient(
          135deg,
          #043b22 0%,
          #065a35 50%,
          #043b22 100%
        );
      }
      .emissions-table tr:hover td {
        background: #f0fdf4 !important;
      }
      .total-banner {
        background: linear-gradient(
          135deg,
          #043b22 0%,
          #065a35 50%,
          #043b22 100%
        );
        position: relative;
        overflow: hidden;
      }
      .total-banner::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
      }
      .answerBox {
        border: 1px solid #dee2e6;
        padding: 1rem;
        margin-bottom: 1rem;
        background-color: #f8f9fa;
        border-radius: 8px;
      }
    `,
  ],
  template: `
    <div
      *ngIf="
        categoryCompleted &&
        (currentCategory === 6 || (isInvitedUser && currentCategory === 2))
      "
      class="summary-container w-full shadow-lg border border-gray-200 p-4 sm:p-6 mb-4 rounded-xl max-w-7xl m-auto"
    >
      <!-- Header Section -->
      <div
        class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2 pb-2 border-b-2 border-[#043B22]/20"
      >
        <div class="flex-1">
          <div class="flex items-center gap-3 mb-2">
            <div
              class="w-10 h-10 bg-[#043B22] rounded-lg flex items-center justify-center"
            >
              <svg
                class="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div class="">
              <div class="futura font-bold text-lg sm:text-xl text-[#043B22]">
                {{ strings.heading_questionnaire_summary }}
              </div>
              <div class="text-[13px] text-[#616161]">
                {{ strings.subheading_questionnaire_summary }}
              </div>
            </div>
          </div>
        </div>
        <div
          *ngIf="calculationName"
          class="flex-shrink-0 bg-gradient-to-br from-[#043B22]/5 to-[#043B22]/15 px-4 py-2 rounded-xl border border-[#043B22]/20 shadow-sm"
        >
          <div
            class="text-[10px] text-[#616161] uppercase tracking-wider font-medium"
          >
            {{ language === 'english' ? 'Calculation' : 'Calcolo' }}
          </div>
          <div
            class="text-[12px] sm:text-[12px] text-[#043B22] font-bold truncate max-w-[180px] sm:max-w-[220px]"
            [title]="calculationName"
          >
            {{ calculationName }}
          </div>
        </div>
      </div>

      <!-- Invited User Subscribe Prompt -->
      <ng-container *ngIf="isInvitedUser">
        <div
          class="flex flex-col items-center justify-center py-12 px-6 text-center"
        >
          <div
            class="w-16 h-16 bg-[#043B22]/10 rounded-full flex items-center justify-center mb-6"
          >
            <svg
              class="w-8 h-8 text-[#043B22]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              ></path>
            </svg>
          </div>
          <h3 class="futura text-xl sm:text-2xl font-bold text-[#043B22] mb-3">
            {{
              language === 'english'
                ? 'Unlock Your Full Report'
                : 'Sblocca il tuo report completo'
            }}
          </h3>
          <p
            class="text-sm sm:text-base text-[#616161] max-w-md mb-8 leading-relaxed"
          >
            {{
              language === 'english'
                ? 'Subscribe to access all 6 categories, generate detailed emission reports, and get a complete carbon footprint analysis for your company.'
                : 'Abbonati per accedere a tutte le 6 categorie, generare report dettagliati sulle emissioni e ottenere un analisi completa dell impronta di carbonio della tua azienda.'
            }}
          </p>
          <a
            routerLink="/dashboard/profile"
            class="inline-flex items-center gap-2 bg-[#043B22] text-white px-8 py-3 rounded-xl futura text-sm sm:text-base font-medium hover:bg-[#065a35] transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              ></path>
            </svg>
            {{ locale.common?.subscribe || 'SUBSCRIBE' }}
          </a>
        </div>
      </ng-container>

      <!-- Full Summary Content (hidden for invited users) -->
      <ng-container *ngIf="!isInvitedUser">
        <!-- Category 1 Header -->
        <div
          class="category-header w-full rounded-lg px-4 py-3 mb-3 cursor-pointer flex items-center justify-between"
          (click)="summaryCategory1 = !summaryCategory1"
        >
          <div
            class="futura flex gap-3 items-center text-sm sm:text-base text-white font-semibold"
          >
            {{ strings.heading_category }} 1
          </div>
          <div class="text-white">
            <app-angle-up *ngIf="summaryCategory1"></app-angle-up>
            <app-angle-down *ngIf="!summaryCategory1"></app-angle-down>
          </div>
        </div>
        <div
          *ngIf="summaryCategory1"
          class="category-content bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm"
        >
          <!-- Question 1 -->
          <div
            class="question-row grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4 p-3 rounded-lg"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[0]?.questions?.[0].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[0]?.questions?.[0].description }}
              </div>
            </div>

            <div class="lg:col-span-2">
              <div *ngIf="sumC1Q1">
                <div
                  class="data-card w-full flex flex-col px-4 py-2 rounded-lg"
                >
                  <div
                    *ngFor="let row of sumC1Q1; let x = index"
                    class="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                  >
                    <div class="flex gap-3 items-center">
                      <div class="w-3 h-3 rounded-full bg-[#043b22]/60"></div>
                      <div class="text-[11px] sm:text-[12px] text-gray-700">
                        {{ row.month }}
                      </div>
                    </div>
                    <div
                      class="text-[12px] sm:text-[13px] font-medium text-[#043B22]"
                    >
                      {{ row.value }} m³
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              (click)="setQuestion(0, 0)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <!-- Question 2 -->
          <div
            class="question-row grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4 p-3 rounded-lg"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[0]?.questions?.[1].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[0]?.questions?.[1].description }}
              </div>
            </div>

            <div class="lg:col-span-2">
              <div *ngIf="sumC1Q2">
                <div
                  class="data-card w-full flex flex-wrap gap-2 px-2 py-2 rounded-lg"
                >
                  <div
                    *ngFor="let row of sumC1Q2; let x = index"
                    class="flex items-center gap-2 bg-gradient-to-r from-[#043b22] to-[#065a35] text-white py-2 px-4 rounded-full shadow-sm"
                  >
                    <span class="text-[11px] sm:text-[12px] font-medium"
                      >{{ row.fuel }}:</span
                    >
                    <span class="text-[11px] sm:text-[12px] font-bold"
                      >{{ row.liters }} {{ strings.liters_short }}</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <div
              (click)="setQuestion(0, 1)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <!-- Question 3 -->
          <div
            class="question-row grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4 p-3 rounded-lg"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[0]?.questions?.[2].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[0]?.questions?.[2].description }}
              </div>
            </div>

            <div class="lg:col-span-2">
              <div *ngIf="sumC1Q3">
                <div
                  class="data-card w-full flex flex-col gap-3 px-4 py-3 rounded-lg"
                >
                  <div
                    *ngFor="let row of sumC1Q3; let x = index"
                    class="flex flex-col gap-1.5 pb-3 border-b border-gray-100 last:border-0 last:pb-0"
                  >
                    <div
                      class="text-[12px] sm:text-[13px] font-semibold futura text-[#043B22] flex items-center gap-2"
                    >
                      {{ strings.vehicle }} {{ x + 1 }}
                    </div>
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.vehicle_type,
                          getLabels(row.vehicle)
                        )
                      "
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(strings.fuel_type, getLabels(row.fuel))
                      "
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.kilometers_per_year,
                          row.kilometers + ' ' + strings.km
                        )
                      "
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.liters_per_year,
                          row.liters + ' ' + strings.liters
                        )
                      "
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div
              (click)="setQuestion(0, 2)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <div
            class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[0]?.questions?.[3].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[0]?.questions?.[3].description }}
              </div>
            </div>

            <div class="lg:col-span-2">
              <div *ngIf="sumC1Q4">
                <div
                  class="data-card w-full flex flex-col gap-3 px-4 py-3 rounded-lg"
                >
                  <div
                    *ngFor="let row of sumC1Q4; let x = index"
                    class="flex flex-col gap-2 my-2 py-0 px-0 rounded-full"
                  >
                    <div
                      class="text-[11px] sm:text-xs font-semibold futura text-[#043B22]"
                    >
                      {{ strings.vehicle }} {{ x + 1 }}
                    </div>
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.vehicle_type,
                          getLabels(row.vehicle)
                        )
                      "
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(strings.fuel_type, getLabels(row.fuel))
                      "
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.kilometers_per_year,
                          row.kilometers + ' ' + strings.km
                        )
                      "
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.liters_per_year,
                          row.liters + ' ' + strings.liters
                        )
                      "
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div
              (click)="setQuestion(0, 3)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <div
            class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[0]?.questions?.[4].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[0]?.questions?.[4].description }}
              </div>
            </div>

            <div class="lg:col-span-2">
              <div *ngIf="sumC1Q5">
                <div
                  class="data-card w-full flex flex-col gap-3 px-4 py-3 rounded-lg"
                >
                  <div
                    *ngFor="let row of sumC1Q5; let x = index"
                    class="flex flex-col gap-2 my-2 py-0 px-0 rounded-full"
                  >
                    <div
                      class="text-[11px] sm:text-xs font-semibold futura text-[#043B22]"
                    >
                      {{ strings.system }} {{ x + 1 }}
                    </div>
                    <div
                      [innerHTML]="getAnswerRow(strings.refills, row.refill)"
                    ></div>
                    <div
                      [innerHTML]="getAnswerRow(strings.gas, row.gas_label)"
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.kg_per_recharge,
                          row.recharge + ' ' + strings.kg
                        )
                      "
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div
              (click)="setQuestion(0, 4)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <div
            class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[0]?.questions?.[5].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[0]?.questions?.[5].description }}
              </div>
            </div>
            <div class="lg:col-span-2">
              <div *ngIf="sumC1Q6">
                <div class="data-card size-full px-4 py-3 rounded-lg">
                  <div
                    [innerHTML]="
                      getAnswerRow(
                        strings.emission,
                        categories[0]?.questions?.[5].answers
                          ?.annual_emissions +
                          ' ' +
                          strings.tonnes
                      )
                    "
                  ></div>
                </div>
              </div>
            </div>
            <div
              (click)="setQuestion(0, 5)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <div
            class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[0]?.questions?.[6].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[0]?.questions?.[6].description }}
              </div>
            </div>
            <div class="lg:col-span-2">
              <div *ngIf="sumC1Q7">
                <div class="data-card size-full px-4 py-3 rounded-lg">
                  <div
                    [innerHTML]="
                      getAnswerRow(
                        strings.emission,
                        categories[0]?.questions?.[6].answers
                          ?.annual_emissions +
                          ' ' +
                          strings.tonnes
                      )
                    "
                  ></div>
                </div>
              </div>
            </div>
            <div
              (click)="setQuestion(0, 6)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <div
            class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[0]?.questions?.[7].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[0]?.questions?.[7].description }}
              </div>
            </div>
            <div class="lg:col-span-2">
              <div *ngIf="sumC1Q8">
                <div
                  class="data-card size-full text-wrap break-words px-4 py-3 rounded-lg"
                >
                  <div
                    [innerHTML]="getAnswerRow(strings.approach, sumC1Q8)"
                  ></div>
                </div>
              </div>
            </div>
            <div
              (click)="setQuestion(0, 7)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
        </div>

        <!-- Category 2 Header -->
        <div
          class="category-header w-full rounded-lg px-4 py-3 mb-3 cursor-pointer flex items-center justify-between"
          (click)="summaryCategory2 = !summaryCategory2"
        >
          <div
            class="futura flex gap-3 items-center text-sm sm:text-base text-white font-semibold"
          >
            {{ strings.heading_category }} 2
          </div>
          <div class="text-white">
            <app-angle-up *ngIf="summaryCategory2"></app-angle-up>
            <app-angle-down *ngIf="!summaryCategory2"></app-angle-down>
          </div>
        </div>
        <div
          *ngIf="summaryCategory2"
          class="category-content bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm"
        >
          <div
            class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
          >
            <!-- Cat 2 Q1 removed per client request
          <div class="lg:col-span-2">
            <div class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]">
              {{ categories[1]?.questions?.[0].title }}
            </div>
            <div class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed">
              {{ categories[1]?.questions?.[0].description }}
            </div>
          </div>
          -->
            <!-- Cat 2 Q1 removed per client request
          <div class="lg:col-span-2">
            <div *ngIf="sumC2Q1">
              <div
                class="data-card w-full flex flex-col px-4 py-3 rounded-lg"
              >
                <div
                  *ngFor="let row of sumC2Q1; let x = index"
                  class="flex justify-between items-center my-2"
                >
                  <div class="grid grid-cols-2 gap-3 w-full">
                    <div class="flex gap-4 items-center">
                      <div
                        class="w-4 h-4 rounded-lg"
                        [ngStyle]="{ background: '#043b22', opacity: 0.5 }"
                      ></div>
                      <div class="text-[10px] sm:text-[11px]">{{ row.month }}</div>
                    </div>
                    <div class="text-[12px]">{{ row.value }} {{ strings.kwh }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            (click)="setQuestion(1, 0)"
            class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
          >
            <app-edit></app-edit>
            <div class="futura">{{ strings.button_edit }}</div>
          </div>
          -->
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <div
            class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[1]?.questions?.[1].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[1]?.questions?.[1].description }}
              </div>
            </div>
            <div class="lg:col-span-2">
              <div *ngIf="sumC2Q2">
                <div
                  class="data-card w-full flex flex-col px-4 py-3 rounded-lg"
                >
                  <div
                    *ngFor="let row of sumC2Q2; let x = index"
                    class="flex justify-between items-center my-2"
                  >
                    <div class="grid grid-cols-3 gap-3 w-full">
                      <div class="flex gap-4 items-center">
                        <div
                          class="w-4 h-4 rounded-lg"
                          [ngStyle]="{ background: '#043b22', opacity: 0.5 }"
                        ></div>
                        <div class="text-[10px] sm:text-[11px]">
                          {{ row.month }}
                        </div>
                      </div>
                      <div class="text-[12px]">
                        {{ row.value }}
                        <span class="text-black" *ngIf="row.showUnit">{{
                          strings.kwh
                        }}</span>
                      </div>
                      <div class="text-[12px]">
                        {{ getElectricitySourceLabel(row.electricitySource) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              (click)="setQuestion(1, 1)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
          <div
            class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
          ></div>
          <div
            class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
          >
            <div class="lg:col-span-2">
              <div
                class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
              >
                {{ categories[1]?.questions?.[2].title }}
              </div>
              <div
                class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
              >
                {{ categories[1]?.questions?.[2].description }}
              </div>
            </div>
            <div class="lg:col-span-2">
              <div *ngIf="sumC2Q3">
                <div
                  class="data-card w-full flex flex-wrap gap-2 px-2 py-2 rounded-lg"
                >
                  <div
                    *ngFor="let row of sumC2Q3; let x = index"
                    class="flex justify-around items-center my-1 bg-[#043b22] text-white uppercase py-2 px-4 rounded-full"
                  >
                    <div class="flex gap-4 items-center">
                      <div class="text-[12px]">
                        {{
                          language === 'english'
                            ? row.label?.english
                            : row.label?.italian
                        }}: &nbsp;
                      </div>
                    </div>
                    <div class="text-[12px]">
                      {{ row.energy }} {{ strings.kwh }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              (click)="setQuestion(1, 2)"
              class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
            >
              <app-edit></app-edit>
              <div class="futura">{{ strings.button_edit }}</div>
            </div>
          </div>
        </div>
        <ng-container *ngIf="!isInvitedUser">
          <!-- Category 3 Header -->
          <div
            class="category-header w-full rounded-lg px-4 py-3 mb-3 cursor-pointer flex items-center justify-between"
            (click)="summaryCategory3 = !summaryCategory3"
          >
            <div
              class="futura flex gap-3 items-center text-sm sm:text-base text-white font-semibold"
            >
              {{ strings.heading_category }} 3
            </div>
            <div class="text-white">
              <app-angle-up *ngIf="summaryCategory3"></app-angle-up>
              <app-angle-down *ngIf="!summaryCategory3"></app-angle-down>
            </div>
          </div>
          <div
            *ngIf="summaryCategory3"
            class="category-content bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm"
          >
            <!-- Question 1 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[2]?.questions?.[0].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[2]?.questions?.[0].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC3Q1 && sumC3Q1.method">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <!-- Method -->
                    <div
                      class="text-[11px] sm:text-xs font-semibold grid grid-cols-2 gap-3"
                    >
                      <span>{{ strings.method }}:</span>
                      <span>
                        {{
                          language === 'english'
                            ? sumC3Q1.method
                            : sumC3Q1.method === 'activity'
                              ? 'attività'
                              : 'spesa'
                        }}
                      </span>
                    </div>

                    <!-- Spend-based method -->
                    <ng-container *ngIf="sumC3Q1.method === 'spend'">
                      <div
                        *ngFor="let item of sumC3Q1.spendData"
                        class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                      >
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.transport_type,
                              item.transportType
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.freight_cost,
                              item.freightCost + ' ' + item.currency
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.period_start,
                              (item.period?.start | date: 'yyyy-MM-dd') || '-'
                            )
                          "
                        ></div>
                      </div>
                    </ng-container>

                    <!-- Activity-based method -->
                    <ng-container *ngIf="sumC3Q1.method === 'activity'">
                      <div
                        *ngFor="let item of sumC3Q1.activityData"
                        class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                      >
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.reference, item.reference)
                          "
                        ></div>
                        <div
                          [innerHTML]="getAnswerRow(strings.date, item.date)"
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.transport_mode,
                              language === 'english'
                                ? item?.transportModeGroup
                                : item?.transportModeGroup
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.distance,
                              item.distance + ' ' + strings.km
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.weight,
                              item.weight + ' ' + strings.kg
                            )
                          "
                        ></div>
                        <div *ngIf="item?.commodity !== '25'">
                          <div
                            [innerHTML]="
                              getAnswerRow(
                                strings.commodity,
                                language === 'english'
                                  ? item?.commodityEnglishLabel
                                  : item?.commodityItalianLabel
                              )
                            "
                          ></div>
                        </div>

                        <div *ngIf="item?.commodity === '25'">
                          <div
                            [innerHTML]="
                              getAnswerRow(
                                strings.commodity,
                                item?.otherCommodity
                              )
                            "
                          ></div>
                        </div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.refrigerated,
                              item.temperatureControlled
                                ? strings.yes
                                : strings.no
                            )
                          "
                        ></div>
                      </div>
                    </ng-container>
                  </div>
                </div>

                <!-- Fallback -->
                <div
                  *ngIf="!sumC3Q1 || !sumC3Q1.method"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_visitor_data }}
                </div>

                <div
                  *ngIf="!sumC3Q1 || sumC3Q1.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_employee_data }}
                </div>
              </div>
              <div
                (click)="setQuestion(2, 0)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <!-- Question 2 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[2]?.questions?.[1].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[2]?.questions?.[1].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC3Q2 && sumC3Q2.method">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <!-- Method -->
                    <div class="text-[11px] sm:text-xs font-semibold">
                      {{ strings.method }}:
                      {{
                        language === 'english'
                          ? sumC3Q2.method
                          : sumC3Q2.method === 'activity'
                            ? 'attività'
                            : 'spesa'
                      }}
                    </div>

                    <!-- Spend-based method -->
                    <ng-container *ngIf="sumC3Q2.method === 'spend'">
                      <div
                        *ngFor="let item of sumC3Q2.spendData"
                        class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                      >
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.transport_type,
                              item.transportType
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.freight_cost,
                              item.freightCost + ' ' + item.currency
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.period_start,
                              (item.period?.start | date: 'yyyy-MM-dd') || '-'
                            )
                          "
                        ></div>
                      </div>
                    </ng-container>

                    <!-- Activity-based method -->
                    <ng-container *ngIf="sumC3Q2.method === 'activity'">
                      <div
                        *ngFor="let item of sumC3Q2.activityData"
                        class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                      >
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.reference, item.reference)
                          "
                        ></div>
                        <div
                          [innerHTML]="getAnswerRow(strings.date, item.date)"
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.transport_mode,
                              language === 'english'
                                ? item?.transportModeGroup
                                : item?.transportModeGroup
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.distance,
                              item.distance + ' ' + strings.km
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.weight,
                              item.weight + ' ' + strings.kg
                            )
                          "
                        ></div>

                        <div
                          class="text-[12px]"
                          *ngIf="item?.commodity !== '25'"
                        >
                          <div
                            [innerHTML]="
                              getAnswerRow(
                                strings.commodity,
                                language === 'english'
                                  ? item?.commodityEnglishLabel
                                  : item?.commodityItalianLabel
                              )
                            "
                          ></div>
                        </div>

                        <div
                          class="text-[12px]"
                          *ngIf="item?.commodity === '25'"
                        >
                          <div
                            [innerHTML]="
                              getAnswerRow(
                                strings.commodity,
                                item?.otherCommodity
                              )
                            "
                          ></div>
                        </div>

                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.refrigerated,
                              item.temperatureControlled
                                ? strings.yes
                                : strings.no
                            )
                          "
                        ></div>
                      </div>
                    </ng-container>
                  </div>
                </div>

                <!-- Fallback -->
                <div
                  *ngIf="!sumC3Q2 || !sumC3Q2.method"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_visitor_data }}
                </div>

                <div
                  *ngIf="!sumC3Q2 || sumC3Q2.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_employee_data }}
                </div>
              </div>
              <div
                (click)="setQuestion(2, 1)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <!-- Question 3 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[2]?.questions?.[2].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[2]?.questions?.[2].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC3Q3 && sumC3Q3.length > 0">
                  <div
                    *ngFor="let row of sumC3Q3"
                    class="entry-block size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.employee_group,
                          row.employeeGroupLabel || row.employeeGroup
                        )
                      "
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(strings.employees, row.employees)
                      "
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(
                          strings.transport,
                          row.transportLabel || row.transport
                        )
                      "
                    ></div>
                    <ng-container *ngIf="row.distance">
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.distance,
                            row.distance + ' ' + strings.km
                          )
                        "
                      ></div>
                    </ng-container>

                    <ng-container *ngIf="row.pointA || row.pointB">
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.point_a,
                            row.pointA || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.point_b,
                            row.pointB || strings.dash
                          )
                        "
                      ></div>
                    </ng-container>

                    <div
                      [innerHTML]="getAnswerRow(strings.trips, row.trips)"
                    ></div>
                    <div
                      [innerHTML]="
                        getAnswerRow(strings.working_hours, row.workingHours)
                      "
                    ></div>

                    <ng-container *ngIf="row.smartWorking">
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.smart_working + ' (%)',
                            row.smartWorking
                          )
                        "
                      ></div>
                    </ng-container>
                  </div>
                </div>

                <div
                  *ngIf="!sumC3Q3 || sumC3Q3.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_employee_data }}
                </div>
              </div>
              <div
                (click)="setQuestion(2, 2)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <!-- Question 4 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[2]?.questions?.[3].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[2]?.questions?.[3].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC3Q4 && sumC3Q4.length > 0">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <div
                      *ngFor="let visitor of sumC3Q4"
                      class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <div class="font-semibold text-[14px] mb-2 capitalize">
                        {{ visitor.visitorGroup || strings.visitor_group }}
                      </div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.departure,
                            visitor.departure || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.destination,
                            visitor.destination || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.transport,
                            visitor.transportMode || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.frequency,
                            visitor.frequency || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.reduction_measures,
                            visitor.reductionMeasures || strings.dash
                          )
                        "
                      ></div>
                    </div>
                  </div>
                </div>
                <div
                  *ngIf="!sumC3Q4 || sumC3Q4.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_visitor_data }}
                </div>
              </div>
              <div
                (click)="setQuestion(2, 3)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <!-- Question 5 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[2]?.questions?.[4].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[2]?.questions?.[4].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC3Q5 && sumC3Q5.length > 0">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <div
                      *ngFor="let trip of sumC3Q5"
                      class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <div class="font-semibold text-[14px] mb-2 capitalize">
                        {{ trip.employeeGroup || strings.business_trip }}
                      </div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.departure,
                            trip.departure || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.destination,
                            trip.destination || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.transport,
                            trip.transportMode.value || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.frequency,
                            trip.frequency || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.room_nights,
                            trip.roomNights || strings.dash
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.reduction_measures,
                            trip.reductionMeasures || strings.dash
                          )
                        "
                      ></div>
                    </div>
                  </div>
                </div>
                <div
                  *ngIf="!sumC3Q5 || sumC3Q5.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_business_trip_data }}
                </div>
              </div>
              <div
                (click)="setQuestion(2, 4)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
          </div>

          <!-- Category 4 Header -->
          <div
            class="category-header w-full rounded-lg px-4 py-3 mb-3 cursor-pointer flex items-center justify-between"
            (click)="summaryCategory4 = !summaryCategory4"
          >
            <div
              class="futura flex gap-3 items-center text-sm sm:text-base text-white font-semibold"
            >
              {{ strings.heading_category }} 4
            </div>
            <div class="text-white">
              <app-angle-up *ngIf="summaryCategory4"></app-angle-up>
              <app-angle-down *ngIf="!summaryCategory4"></app-angle-down>
            </div>
          </div>
          <div
            *ngIf="summaryCategory4"
            class="category-content bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm"
          >
            <!-- Question 1 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[3]?.questions?.[0].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[3]?.questions?.[0].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC4Q1 && sumC4Q1.length > 0">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <div
                      *ngFor="let group of sumC4Q1"
                      class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.main_category,
                            language === 'english'
                              ? group.main_category_info?.name
                              : group.main_category_info?.itaName
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.region,
                            group.spend_region | locodeToCountryName
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.amount,
                            group.money_amount + ' ' + group.money_unit
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(strings.year, group.spend_year)
                        "
                      ></div>
                      <div
                        [innerHTML]="getAnswerRow(strings.note, group.note)"
                      ></div>
                    </div>
                  </div>
                </div>

                <div
                  *ngIf="!sumC4Q1 || sumC4Q1.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_employee_data }}
                </div>
              </div>
              <div
                (click)="setQuestion(3, 0)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <!-- Question 2 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[3]?.questions?.[1].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[3]?.questions?.[1].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC4Q2 && sumC4Q2.length > 0">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <div
                      *ngFor="let group of sumC4Q2"
                      class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.main_category,
                            language === 'english'
                              ? group.main_category_info?.name
                              : group.main_category_info?.itaName
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.region,
                            group.spend_region | locodeToCountryName
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.amount,
                            group.money_amount + ' ' + group.money_unit
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(strings.year, group.spend_year)
                        "
                      ></div>
                      <div
                        [innerHTML]="getAnswerRow(strings.note, group.note)"
                      ></div>
                    </div>
                  </div>
                </div>
                <div
                  *ngIf="!sumC4Q2 || sumC4Q2.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_remote_worker_data }}
                </div>
              </div>
              <div
                (click)="setQuestion(3, 1)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <!-- Question 3 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[3]?.questions?.[2].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[3]?.questions?.[2].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC4Q3 && sumC4Q3.length > 0">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <div
                      *ngFor="let group of sumC4Q3"
                      class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <!-- Waste-type treatment block -->
                      <div *ngIf="group.material_treatment">
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.main_category,
                              language === 'english'
                                ? group.main_category_info?.label
                                : group.main_category_info?.itaLabel
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.treatment,
                              language === 'english'
                                ? group.material_treatment_info?.label
                                : group.material_treatment_info?.itaLabel
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.quantity,
                              group.quantity + ' ' + group.unit
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.period, group.period)
                          "
                        ></div>
                      </div>

                      <!-- Spend-based wastewater treatment block -->
                      <div *ngIf="group.currency && !group.material_treatment">
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.field, group.FieldLabel)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.amount, group.currency)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.period, group.period)
                          "
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  *ngIf="!sumC4Q1 || sumC4Q1.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_employee_data }}
                </div>
              </div>
              <div
                (click)="setQuestion(3, 2)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[3]?.questions?.[3].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[3]?.questions?.[3].description }}
                </div>
              </div>

              <div class="lg:col-span-2">
                <div *ngIf="sumC4Q4 && sumC4Q4.usage">
                  <div
                    class="bg-white flex flex-col gap-4 px-4 py-4 border border-gray-200 rounded-lg my-2 answerBox"
                  >
                    <ng-container *ngIf="sumC4Q4.selectedMethod === 'average'">
                      <div *ngFor="let item of sumC4Q4.averageBuildingControls">
                        <div class="grid grid-cols-2 gap-3">
                          <span>{{ strings.building }}:</span>
                          <span>{{ item.assetLabel }}</span>
                        </div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.type,
                              language === 'english'
                                ? item.buildingType_info?.label
                                : item.buildingType_info?.itaLabel
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.epc_rating,
                              language === 'english'
                                ? item.epcRating_info?.label
                                : item.epcRating_info?.itaLabel
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.country,
                              item.country | locodeToCountryName
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.year, item.reportingYear)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.floor_area, item.floorArea)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.method, item.method)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.asset_type,
                              language === 'english'
                                ? item.assetType
                                : item.assetType
                            )
                          "
                        ></div>
                      </div>

                      <div *ngFor="let item of sumC4Q4.averageVehicleControls">
                        <div class="grid grid-cols-2 gap-3">
                          <span>{{ strings.vehicle }}:</span>
                          <span>{{ item.assetLabel }}</span>
                        </div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.category,
                              language === 'english'
                                ? item.assetCategory_info?.label
                                : item.assetCategory_info?.itaLabel
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.subtype,
                              language === 'english'
                                ? item.subtype_info?.label
                                : item.subtype_info?.itaLabel
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.country,
                              item.country | locodeToCountryName
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.year, item.reportingYear)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.quantity, item.quantity)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.annual_distance,
                              item.annualDistance
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.method, item.method)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.asset_type,
                              language === 'english'
                                ? item.assetType
                                : item.assetType
                            )
                          "
                        ></div>
                      </div>
                    </ng-container>
                    <ng-container
                      *ngIf="sumC4Q4.selectedMethod === 'asset-specific'"
                    >
                      <div
                        *ngFor="
                          let item of sumC4Q4.assetSpecificBuildingControls
                        "
                      >
                        <div class="grid grid-cols-2 gap-3">
                          <span>{{ strings.building }}:</span>
                          <span>{{ item.assetLabel }}</span>
                        </div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.country,
                              item.country | locodeToCountryName
                            )
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.year, item.reportingYear)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.sub_metered, item.subMetered)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(strings.method, item.method)
                          "
                        ></div>
                        <div
                          [innerHTML]="
                            getAnswerRow(
                              strings.asset_type,
                              language === 'english'
                                ? item.assetType_info?.label
                                : item.assetType_info?.itaLabel
                            )
                          "
                        ></div>

                        <div
                          *ngFor="let energy of item.energyEntries"
                          class="pl-4 mt-2 border-l border-gray-300"
                        >
                          <div
                            [innerHTML]="
                              getAnswerRow(
                                strings.category,
                                language === 'english'
                                  ? energy.category_info?.name
                                  : energy.category_info?.itaName
                              )
                            "
                          ></div>
                          <div
                            [innerHTML]="
                              getAnswerRow(
                                strings.type,
                                language === 'english'
                                  ? energy.type_info?.label
                                  : energy.type_info?.itaLabel
                              )
                            "
                          ></div>
                          <div
                            [innerHTML]="
                              getAnswerRow(strings.amount, energy.amount)
                            "
                          ></div>
                          <div
                            [innerHTML]="
                              getAnswerRow(strings.unit, energy.unit)
                            "
                          ></div>
                          <div
                            [innerHTML]="
                              getAnswerRow(
                                strings.sub_metered,
                                energy.isSubMetered
                              )
                            "
                          ></div>
                        </div>
                      </div>

                      <div
                        *ngFor="
                          let item of sumC4Q4.assetSpecificVehicleControls
                        "
                      >
                        <div class="grid grid-cols-2 gap-3">
                          <span>{{ strings.vehicle }}:</span>
                          <span>{{ item.assetLabel }}</span>
                        </div>
                        <div class="text-[12px]">
                          {{ strings.country }}:
                          {{ item.country | locodeToCountryName }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.year }}: {{ item.reportingYear }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.subtype }}:
                          {{
                            language === 'english'
                              ? item.subtype_info?.label
                              : item.subtype_info?.itaLabel
                          }}
                        </div>
                        <div class="text-[12px]">
                          <span class="text-[12px] text-[#616161]"
                            >{{ strings.quantity }}:</span
                          >
                          {{ item.quantity }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.annual_distance }}:
                          {{ item.annualDistance }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.method }}: {{ item.method }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.asset_type }}:
                          {{
                            language === 'english'
                              ? item.assetType_info?.label
                              : item.assetType_info?.itaLabel
                          }}
                        </div>
                      </div>
                    </ng-container>
                    <ng-container
                      *ngIf="sumC4Q4.selectedMethod === 'lessor-specific'"
                    >
                      <div *ngFor="let item of sumC4Q4.lessorSpecificControls">
                        {{ strings.lessor }}: {{ item.lessorName }}<br />
                        <div class="text-[12px]">
                          {{ strings.asset_type }}:
                          {{
                            language === 'english'
                              ? item.assetType_info?.label
                              : item.assetType_info?.itaLabel
                          }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.country }}:
                          {{ item.country | locodeToCountryName }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.year }}: {{ item.reportingYear }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.scope1_emissions }}:
                          {{ item.scope1Emissions }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.scope2_emissions }}:
                          {{ item.scope2Emissions }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.leased_quantity }}:
                          {{ item.leasedQuantity }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.total_quantity }}: {{ item.totalQuantity }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.occupancy_rate }}: {{ item.occupancyRate }}
                        </div>
                        <div class="text-[12px]">
                          {{ strings.method }}: {{ item.method }}
                        </div>
                      </div>
                    </ng-container>
                  </div>
                </div>
                <div
                  *ngIf="!sumC4Q4 || !sumC4Q4.usage"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_visitor_data }}
                </div>
              </div>

              <div
                (click)="setQuestion(3, 3)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <!-- Question 5 -->
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[3]?.questions?.[4].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[3]?.questions?.[4].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC4Q5 && sumC4Q5.length > 0">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <div
                      *ngFor="let trip of sumC4Q5"
                      class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.main_category,
                            language === 'english'
                              ? trip.purchase_category_info?.label
                              : trip.purchase_category_info?.itaLabel
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.region,
                            trip.spend_region | locodeToCountryName
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(
                            strings.amount,
                            trip.money_amount + ' ' + trip.money_unit
                          )
                        "
                      ></div>
                      <div
                        [innerHTML]="
                          getAnswerRow(strings.year, trip.spend_year)
                        "
                      ></div>
                      <div
                        [innerHTML]="getAnswerRow(strings.note, trip.note)"
                      ></div>
                    </div>
                  </div>
                </div>
                <div
                  *ngIf="!sumC4Q5 || sumC4Q5.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_data_entered }}
                </div>
              </div>
              <div
                (click)="setQuestion(3, 4)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
          </div>
          <!-- Category 5 Header -->
          <div
            class="category-header w-full rounded-lg px-4 py-3 mb-3 cursor-pointer flex items-center justify-between"
            (click)="summaryCategory5 = !summaryCategory5"
          >
            <div
              class="futura flex gap-3 items-center text-sm sm:text-base text-white font-semibold"
            >
              {{ strings.heading_category }} 5
            </div>
            <div class="text-white">
              <app-angle-up *ngIf="summaryCategory5"></app-angle-up>
              <app-angle-down *ngIf="!summaryCategory5"></app-angle-down>
            </div>
          </div>
          <div
            *ngIf="summaryCategory5"
            class="category-content bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm"
          >
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[4]?.questions?.[0].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[4]?.questions?.[0].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC5Q1?.usage" class="flex flex-col gap-4">
                  <div
                    *ngFor="let product of sumC5Q1.productGroups; let i = index"
                    class="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div class="font-semibold text-[14px] mb-2">
                      {{ strings.product_group }} {{ i + 1 }}:
                      {{ product.product_label }}
                    </div>

                    <div
                      class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                    >
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.units_sold }}:</span
                        >
                        {{ product.N_sold }} {{ product.units_sold_unit }}
                      </div>
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.region }}:</span
                        >
                        {{ product.region | locodeToCountryName }}
                      </div>
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.product_lifetime }}:</span
                        >
                        {{ product.U_life }}
                      </div>
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.final_lifetime }}:</span
                        >
                        {{ product.U_life_final }}
                      </div>
                      <div *ngIf="product.note" class="lg:col-span-2">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.note }}:</span
                        >
                        {{ product.note }}
                      </div>
                    </div>

                    <div
                      *ngIf="product.intermed_label"
                      class="mt-4 border-t pt-4"
                    >
                      <div class="font-medium mb-2">
                        {{ strings.intermediate_product }}
                      </div>
                      <div
                        class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                      >
                        <div class="text-[12px]">
                          <span class="text-[12px] text-[#616161]"
                            >{{ strings.label }}:</span
                          >
                          {{ product.intermed_label }}
                        </div>
                        <div class="text-[12px]">
                          <span class="text-[12px] text-[#616161]"
                            >{{ strings.amount_sold }}:</span
                          >
                          {{ product.intermed_sold }}
                          {{ product.intermed_unit }}
                        </div>
                        <div class="text-[12px]">
                          <span class="text-[12px] text-[#616161]"
                            >{{ strings.ef_per_use }}:</span
                          >
                          {{ product.EF_per_use_intermed }}
                        </div>
                      </div>
                    </div>

                    <div class="mt-4 border-t pt-4">
                      <div class="font-medium mb-2">{{ strings.methods }}</div>

                      <div
                        *ngIf="product.use_m1 && product.m1_methods?.length"
                        class="mb-4"
                      >
                        <div class="font-semibold text-[14px]">
                          {{ strings.method }} 1: {{ strings.fuel_combustion }}
                        </div>
                        <div
                          *ngFor="let method of product.m1_methods"
                          class="ml-4 mt-2"
                        >
                          <div class="grid grid-cols-2 gap-2 text-sm">
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.fuel_type }}:</span
                              >
                              {{ method.fuel_type }}
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.lifetime_quantity }}:</span
                              >
                              {{ method.fuel_lifetime_quantity }}
                              {{ method.fuel_lifetime_unit }}
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.electricity }}:</span
                              >
                              {{ method.elec_lifetime_kwh }} kWh
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.refrigerant }}:</span
                              >
                              {{ method.ref_gas_type }}
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.charge }}:</span
                              >
                              {{ method.ref_charge_kg }} kg
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.leakage }}:</span
                              >
                              {{ method.ref_leak_pct }}%
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        *ngIf="product.use_m2 && product.m2_methods?.length"
                        class="mb-4"
                      >
                        <div class="font-semibold text-[14px]">
                          {{ strings.method }} 2: {{ strings.direct_fuel_use }}
                        </div>
                        <div
                          *ngFor="let method of product.m2_methods"
                          class="ml-4 mt-2"
                        >
                          <div class="grid grid-cols-2 gap-2 text-sm">
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.fuel_type }}:</span
                              >
                              {{ method.fuel_type }}
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.quantity }}:</span
                              >
                              {{ method.fuel_qty }} {{ method.fuel_unit }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        *ngIf="product.use_m3 && product.m3_methods?.length"
                        class="mb-4"
                      >
                        <div class="font-semibold text-[14px]">
                          {{ strings.method }} 3:
                          {{ strings.refrigerant_leakage }}
                        </div>
                        <div
                          *ngFor="let method of product.m3_methods"
                          class="ml-4 mt-2"
                        >
                          <div class="grid grid-cols-2 gap-2 text-sm">
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.gas_type }}:</span
                              >
                              {{ method.gas_type }}
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.charge }}:</span
                              >
                              {{ method.gas_charge_kg }} kg
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.leakage }}:</span
                              >
                              {{ method.gas_leak_pct }}%
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        *ngIf="product.use_m4 && product.m4_methods?.length"
                        class="mb-4"
                      >
                        <div class="font-semibold text-[14px]">
                          {{ strings.method }} 4: {{ strings.scenario_based }}
                        </div>
                        <div
                          *ngFor="let method of product.m4_methods"
                          class="ml-4 mt-2"
                        >
                          <div class="grid grid-cols-2 gap-2 text-sm">
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.scenario }}:</span
                              >
                              {{ method.scn_label }}
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.share }}:</span
                              >
                              {{ method.scn_share_pct }}%
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.fuel_type }}:</span
                              >
                              {{ method.scn_fuel_type }}
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.fuel_qty }}:</span
                              >
                              {{ method.scn_fuel_qty }}
                              {{ method.scn_fuel_unit }}
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.electricity }}:</span
                              >
                              {{ method.scn_elec_kwh }} kWh
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.refrigerant_leak }}:</span
                              >
                              {{ method.scn_ref_leak_kg }} kg
                            </div>
                            <div class="text-[12px]">
                              <span class="text-[12px] text-[#616161]"
                                >{{ strings.ghg }}:</span
                              >
                              {{ method.scn_ghg_kg }} kg
                              {{ method.scn_ghg_type }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div *ngIf="product.use_m5" class="mb-4">
                        <div class="font-semibold text-[14px]">
                          {{ strings.method }} 5:
                          {{ strings.intermediate_product }}
                        </div>
                        <div class="ml-4 mt-2 text-sm">
                          {{ strings.calculated_from_intermediate }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  *ngIf="!sumC5Q1?.usage"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_data_available }}
                </div>
              </div>
              <div
                (click)="setQuestion(4, 0)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[4]?.questions?.[1].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[4]?.questions?.[1].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div
                  *ngIf="sumC5Q2 && sumC5Q2.usage"
                  class="flex flex-col gap-4"
                >
                  <div class="font-semibold text-[14px]">
                    {{ strings.selected_method }}:
                    {{ sumC5Q2.selectedMethod | titlecase }}
                  </div>

                  <div
                    *ngIf="sumC5Q2.selectedMethod === 'average'"
                    class="space-y-4"
                  >
                    <div
                      *ngIf="sumC5Q2.averageLeasedBuildingControls?.length > 0"
                    >
                      <div class="font-medium">
                        {{ strings.leased_buildings }}
                      </div>

                      <div
                        *ngFor="
                          let building of sumC5Q2.averageLeasedBuildingControls
                        "
                        class="data-card bg-white rounded-lg p-4 mt-2"
                      >
                        <div
                          class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                        >
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.label }}:</span
                            >
                            {{ building.assetLabel || strings.na }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.type }}:</span
                            >
                            {{ getLabel(buildingTypes, building.buildingType) }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.epc_rating }}:</span
                            >
                            {{ getLabel(epcRatings, building.epcRating) }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.country }}:</span
                            >
                            <!-- {{ getLabel(countries, building.country) }} -->
                            {{ building.country | locodeToCountryName }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.year }}:</span
                            >
                            {{ building.reportingYear || strings.na }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.floor_area }}:</span
                            >
                            {{ building.floorArea || strings.na }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lessee }}:</span
                            >
                            {{ building.lesseeEntity || strings.na }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lease_period }}:</span
                            >
                            {{
                              building.leaseStartDate
                                ? (building.leaseStartDate | date)
                                : strings.na
                            }}
                            -
                            {{
                              building.leaseEndDate
                                ? (building.leaseEndDate | date)
                                : strings.na
                            }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      *ngIf="
                        sumC5Q2.averageLeasedOtherAssetControls?.length > 0
                      "
                    >
                      <div class="font-medium">
                        {{ strings.leased_other_assets }}
                      </div>
                      <div
                        *ngFor="
                          let asset of sumC5Q2.averageLeasedOtherAssetControls
                        "
                        class="data-card bg-white rounded-lg p-4 mt-2"
                      >
                        <div
                          class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                        >
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.label }}:</span
                            >
                            {{ asset.assetLabel || strings.na }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.category }}:</span
                            >
                            {{
                              getLabel(vehicleCategories, asset.assetCategory)
                            }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.subtype }}:</span
                            >
                            {{
                              getVehicleSubtypeLabel(
                                asset.assetCategory,
                                asset.subtype
                              )
                            }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.country }}:</span
                            >
                            <!-- {{ getLabel(countries, asset.country) }} -->
                            {{ asset.country | locodeToCountryName }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.year }}:</span
                            >
                            {{ asset.reportingYear || strings.na }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.utilization }}:</span
                            >
                            {{
                              getLabel(
                                utilizationMetrics,
                                asset.utilizationMetric
                              )
                            }}
                            <span *ngIf="asset.quantity"
                              >({{ asset.quantity }})</span
                            >
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lessee }}:</span
                            >
                            {{ asset.lesseeEntity || strings.na }}
                          </div>

                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lease_period }}:</span
                            >
                            {{
                              asset.leaseStartDate
                                ? (asset.leaseStartDate | date)
                                : strings.na
                            }}
                            -
                            {{
                              asset.leaseEndDate
                                ? (asset.leaseEndDate | date)
                                : strings.na
                            }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    *ngIf="sumC5Q2.selectedMethod === 'asset-specific'"
                    class="space-y-4"
                  >
                    <div
                      *ngIf="
                        sumC5Q2.assetSpecificLeasedBuildingControls?.length > 0
                      "
                    >
                      <div class="font-medium">
                        {{ strings.leased_buildings }}
                      </div>
                      <div
                        *ngFor="
                          let building of sumC5Q2.assetSpecificLeasedBuildingControls
                        "
                        class="data-card bg-white rounded-lg p-4 mt-2"
                      >
                        <div
                          class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                        >
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.label }}:</span
                            >
                            {{ building.assetLabel || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lessee }}:</span
                            >
                            {{ building.lesseeEntity || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.year }}:</span
                            >
                            {{ building.reportingYear || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.country }}:</span
                            >
                            <!-- {{ getLabel(countries, building.country) }} -->
                            {{ building.country | locodeToCountryName }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lease_period }}:</span
                            >
                            {{ building.leaseStartDate | date }} -
                            {{ building.leaseEndDate | date }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.sub_metered }}:</span
                            >
                            {{ building.subMetered ? strings.yes : strings.no }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.occupancy_rate }}:</span
                            >
                            {{ building.occupancyRate || strings.na }}
                          </div>
                        </div>

                        <div
                          *ngIf="building.entries?.length > 0"
                          class="mt-4 border-t pt-4"
                        >
                          <div class="font-medium">{{ strings.entries }}</div>
                          <div
                            *ngFor="let entry of building.entries"
                            class="ml-4 mt-2"
                          >
                            <div
                              class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                            >
                              <div class="text-[12px]">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.category }}:</span
                                >
                                {{
                                  getLabel(
                                    leasedAssetCategories,
                                    entry.category
                                  )
                                }}
                              </div>
                              <div class="text-[12px]">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.type }}:</span
                                >
                                {{ getLabel(fuelTypes, entry.type) }}
                              </div>
                              <div class="text-[12px]">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.unit }}:</span
                                >
                                {{ getLabel(fuelUnits, entry.unit) }}
                              </div>
                              <div *ngIf="entry.amount">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.amount }}:</span
                                >
                                {{ entry.amount }}
                              </div>
                              <div *ngIf="entry.totalBuildingEnergy">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.total_energy }}:</span
                                >
                                {{ entry.totalBuildingEnergy }}
                              </div>
                              <div *ngIf="entry.totalBuildingArea">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.total_area }}:</span
                                >
                                {{ entry.totalBuildingArea }}
                              </div>
                              <div *ngIf="entry.leasedArea">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.leased_area }}:</span
                                >
                                {{ entry.leasedArea }}
                              </div>
                              <div class="text-[12px]">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.sub_metered }}:</span
                                >
                                {{
                                  entry.isSubMetered ? strings.yes : strings.no
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      *ngIf="
                        sumC5Q2.assetSpecificLeasedOtherAssetControls?.length >
                        0
                      "
                    >
                      <div class="font-medium">
                        {{ strings.leased_other_assets }}
                      </div>
                      <div
                        *ngFor="
                          let asset of sumC5Q2.assetSpecificLeasedOtherAssetControls
                        "
                        class="data-card bg-white rounded-lg p-4 mt-2"
                      >
                        <div
                          class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                        >
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.label }}:</span
                            >
                            {{ asset.assetLabel || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lessee }}:</span
                            >
                            {{ asset.lesseeEntity || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.annual_distance }}:</span
                            >
                            {{ asset.annualDistance || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.category }}:</span
                            >
                            {{
                              getLabel(
                                leasedAssetCategories,
                                asset.assetCategory
                              )
                            }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.subtype }}:</span
                            >
                            {{
                              getVehicleSubtypeLabel(
                                asset.assetCategory,
                                asset.subtype
                              )
                            }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.year }}:</span
                            >
                            {{ asset.reportingYear || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.country }}:</span
                            >
                            <!-- {{ getLabel(countries, asset.country) }} -->
                            {{ asset.country | locodeToCountryName }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lease_period }}:</span
                            >
                            {{
                              asset.leaseStartDate
                                ? (asset.leaseStartDate | date)
                                : strings.na
                            }}
                            -
                            {{
                              asset.leaseEndDate
                                ? (asset.leaseEndDate | date)
                                : strings.na
                            }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.utilization_metric }}:</span
                            >
                            {{
                              getLabel(
                                utilizationMetrics,
                                asset.utilizationMetric
                              )
                            }}
                          </div>
                        </div>

                        <div
                          *ngIf="asset.energyEntries?.length > 0"
                          class="mt-4 border-t pt-4"
                        >
                          <div class="font-medium">
                            {{ strings.energy_entries }}
                          </div>
                          <div
                            *ngFor="let entry of asset.energyEntries"
                            class="ml-4 mt-2"
                          >
                            <div
                              class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                            >
                              <div class="text-[12px]">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.fuel_type }}:</span
                                >
                                {{ getLabel(fuelTypes, entry.fuelType) }}
                              </div>
                              <div class="text-[12px]">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.unit }}:</span
                                >
                                {{ getLabel(fuelUnits, entry.unit) }}
                              </div>
                              <div class="text-[12px]">
                                <span class="text-[12px] text-[#616161]"
                                  >{{ strings.amount }}:</span
                                >
                                {{ entry.amount || strings.na }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    *ngIf="sumC5Q2.selectedMethod === 'lessee-specific'"
                    class="space-y-4"
                  >
                    <div *ngIf="sumC5Q2.lesseeSpecificControls?.length > 0">
                      <div
                        *ngFor="let control of sumC5Q2.lesseeSpecificControls"
                        class="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div
                          class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                        >
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.asset_label }}:</span
                            >
                            {{ control.assetLabel || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.asset_type }}:</span
                            >
                            {{ control.assetType || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lessee_entity }}:</span
                            >
                            {{ control.lesseeEntity || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.reporting_year }}:</span
                            >
                            {{ control.reportingYear || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.country }}:</span
                            >
                            {{ control.country | locodeToCountryName }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.lease_period }}:</span
                            >
                            {{ control.leaseStartDate | date }} -
                            {{ control.leaseEndDate | date }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.scope1_emissions }}:</span
                            >
                            {{ control.scope1Emissions || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.scope2_emissions }}:</span
                            >
                            {{ control.scope2Emissions || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.scope2_basis }}:</span
                            >
                            {{
                              getLabel(scope2BasisOptions, control.scope2Basis)
                            }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.allocation_metric }}:</span
                            >
                            {{ control.allocationMetric || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.leased_quantity }}:</span
                            >
                            {{ control.leasedQuantity || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.total_quantity }}:</span
                            >
                            {{ control.totalQuantity || strings.na }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.occupancy_rate }}:</span
                            >
                            {{ control.occupancyRate || strings.na }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  *ngIf="!sumC5Q2?.usage"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_data_available }}
                </div>
              </div>
              <div
                (click)="setQuestion(4, 1)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[4].questions?.[2].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[4].questions?.[2].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div
                  *ngIf="sumC5Q3 && sumC5Q3.length > 0"
                  class="flex flex-col gap-4"
                >
                  <div
                    *ngFor="let product of sumC5Q3; let i = index"
                    class="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div class="font-semibold text-[14px] mb-2">
                      {{ strings.product_group }} {{ i + 1 }}:
                      {{ product.product_label }}
                    </div>

                    <div
                      class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                    >
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.mass_sold }}:</span
                        >
                        {{ product.mass_sold }} {{ product.mass_unit }}
                      </div>
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.year }}:</span
                        >
                        {{ product.rep_year }}
                      </div>
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.region }}:</span
                        >
                        {{ product.region | locodeToCountryName }}
                      </div>
                      <div *ngIf="product.note">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.note }}:</span
                        >
                        {{ product.note }}
                      </div>
                    </div>

                    <div
                      *ngIf="product.eol_methods?.length > 0"
                      class="mt-4 border-t pt-4"
                    >
                      <div class="font-medium mb-2">
                        {{ strings.eol_treatment_methods }}
                      </div>
                      <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                          <thead class="bg-gray-50">
                            <tr>
                              <th
                                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {{ strings.method }}
                              </th>
                              <th
                                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {{ strings.waste_type }}
                              </th>
                              <th
                                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {{ strings.share }}
                              </th>
                            </tr>
                          </thead>
                          <tbody class="bg-white divide-y divide-gray-200">
                            <tr *ngFor="let method of product.eol_methods">
                              <td class="px-4 py-2 whitespace-nowrap">
                                {{ method.eol_method }}
                              </td>
                              <td class="px-4 py-2 whitespace-nowrap">
                                {{
                                  language === 'english'
                                    ? method.waste_type_label_en
                                    : method.waste_type_label_it
                                }}
                              </td>
                              <td class="px-4 py-2 whitespace-nowrap">
                                {{ method.share_pct }}%
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  *ngIf="!sumC5Q3 || sumC5Q3.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_data_available }}
                </div>
              </div>
              <div
                (click)="setQuestion(4, 2)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
            <div
              class="w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent h-px my-3"
            ></div>

            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[4].questions?.[3].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[4].questions?.[3].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="sumC5Q4" class="flex flex-col gap-4">
                  <div
                    *ngFor="let product of sumC5Q4"
                    class="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div class="font-semibold text-[14px] mb-2">
                      {{ product.product_label }}
                    </div>

                    <div
                      class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                    >
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.year }}:</span
                        >
                        {{ product.rep_year }}
                      </div>
                      <div class="text-[12px]">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.currency }}:</span
                        >
                        {{ product.currency }}
                      </div>
                      <div *ngIf="product.note" class="lg:col-span-2">
                        <span class="text-[12px] text-[#616161]"
                          >{{ strings.note }}:</span
                        >
                        {{ product.note }}
                      </div>
                    </div>

                    <div
                      *ngIf="product.investments?.length > 0"
                      class="mt-4 border-t pt-4"
                    >
                      <div class="font-medium mb-2">
                        {{ strings.investments }}
                      </div>
                      <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                          <thead class="bg-gray-50">
                            <tr>
                              <th
                                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {{ strings.asset_class }}
                              </th>
                              <th
                                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {{ strings.investment_value }}
                              </th>
                              <th
                                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {{ strings.ef_override }}
                              </th>
                              <th
                                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {{ strings.share }}
                              </th>
                            </tr>
                          </thead>
                          <tbody class="bg-white divide-y divide-gray-200">
                            <tr *ngFor="let investment of product.investments">
                              <td class="px-4 py-2 whitespace-nowrap">
                                {{ formatAssetClass2(investment.asset_class) }}
                              </td>
                              <td class="px-4 py-2 whitespace-nowrap">
                                {{ investment.inv_value }}
                                {{ product.currency }}
                              </td>
                              <td class="px-4 py-2 whitespace-nowrap">
                                {{ investment.ef_override }}
                              </td>
                              <td class="px-4 py-2 whitespace-nowrap">
                                {{ investment.share_pct || strings.na }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  *ngIf="!sumC5Q4 || sumC5Q4.length === 0"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_data_available }}
                </div>
              </div>
              <div
                (click)="setQuestion(4, 3)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
          </div>
          <!-- Category 6 Header -->
          <div
            class="category-header w-full rounded-lg px-4 py-3 mb-3 cursor-pointer flex items-center justify-between"
            (click)="summaryCategory6 = !summaryCategory6"
          >
            <div
              class="futura flex gap-3 items-center text-sm sm:text-base text-white font-semibold"
            >
              {{ strings.heading_category }} 6
            </div>
            <div class="text-white">
              <app-angle-up *ngIf="summaryCategory6"></app-angle-up>
              <app-angle-down *ngIf="!summaryCategory6"></app-angle-down>
            </div>
          </div>
          <div
            *ngIf="summaryCategory6"
            class="category-content bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm"
          >
            <div
              class="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_1fr_80px] gap-4"
            >
              <div class="lg:col-span-2">
                <div
                  class="futura text-[13px] sm:text-[14px] font-semibold text-[#043B22]"
                >
                  {{ categories[5]?.questions?.[0].title }}
                </div>
                <div
                  class="text-[11px] sm:text-[12px] text-[#616161] mt-1.5 leading-relaxed"
                >
                  {{ categories[5]?.questions?.[0].description }}
                </div>
              </div>
              <div class="lg:col-span-2">
                <div *ngIf="hasValidData(sumC6Q1)">
                  <div
                    class="size-full bg-white flex flex-col gap-2 px-4 py-2.5 shadow-sm my-2 answerBox"
                  >
                    <div
                      *ngFor="let item of sumC6Q1"
                      class="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                    >
                      <div
                        class="grid grid-cols-2 gap-3 text-[10px] sm:text-[11px]"
                      >
                        <div class="text-[12px]">
                          <span class="text-[12px] text-[#616161]"
                            >{{ strings.description }}:</span
                          >
                          {{ item.description || strings.na }}
                        </div>
                        <div class="text-[12px]">
                          <span class="text-[12px] text-[#616161]"
                            >{{ strings.activity_value }}:</span
                          >
                          {{ item.activity_value
                          }}{{ getActivityUnitLabel(item.activity_unit) }}
                        </div>
                        <div class="text-[12px]">
                          <span class="text-[12px] text-[#616161]"
                            >{{ strings.unit_type }}:</span
                          >
                          {{ getUnitLabel(item.unit_type) || strings.na }}
                        </div>

                        <ng-container *ngIf="!item.custom_factor">
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.factor_value }}:</span
                            >
                            {{ item.factor_value || strings.na }}
                          </div>
                        </ng-container>

                        <ng-container *ngIf="item.custom_factor">
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.custom_value }}:</span
                            >
                            {{ item.custom_value }} {{ item.factor_unit }}
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.source }}:</span
                            >
                            <a
                              [href]="item.factor_source"
                              target="_blank"
                              class="text-[#043b22] hover:underline"
                            >
                              {{ item.factor_source || strings.na }}
                            </a>
                          </div>
                          <div class="text-[12px]">
                            <span class="text-[12px] text-[#616161]"
                              >{{ strings.justification }}:</span
                            >
                            {{ item.justification || strings.na }}
                          </div>
                        </ng-container>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  *ngIf="!hasValidData(sumC6Q1)"
                  class="text-[#616161] text-[12px] italic"
                >
                  {{ strings.no_data_available }}
                </div>
              </div>
              <div
                (click)="setQuestion(5, 0)"
                class="edit-btn flex gap-2 justify-start lg:justify-end items-center font-bold text-[#043b22] cursor-pointer text-[12px] hover:text-[#065a35]"
              >
                <app-edit></app-edit>
                <div class="futura">{{ strings.button_edit }}</div>
              </div>
            </div>
          </div>
        </ng-container>
        <!-- CATEGORY TOTALS TABLE -->
        <div
          class="mt-8 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
        >
          <div
            class="flex items-center gap-3 px-5 py-4 border-b bg-gradient-to-r from-gray-50 to-white"
          >
            <div
              class="w-8 h-8 bg-[#043B22]/10 rounded-lg flex items-center justify-center"
            >
              <svg
                class="w-4 h-4 text-[#043B22]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                ></path>
              </svg>
            </div>
            <div
              class="futura text-[14px] sm:text-[15px] font-semibold text-[#043B22]"
            >
              {{ strings.categorySummaryTitle }}
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="emissions-table w-full text-[12px] sm:text-[13px]">
              <thead class="bg-[#043B22]">
                <tr class="bg-[#043B22] text-white">
                  <th class="px-4 py-3 text-left font-semibold">
                    {{ strings.categoryLabel }}
                  </th>
                  <th class="px-4 py-3 text-right font-semibold">CO₂ (t)</th>
                  <th class="px-4 py-3 text-right font-semibold">CH₄ (t)</th>
                  <th class="px-4 py-3 text-right font-semibold">N₂O (t)</th>
                  <th class="px-4 py-3 text-right font-semibold">CO₂e (t)</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let category of categories; let i = index"
                  [hidden]="isInvitedUser && i >= 2"
                  class="border-t border-gray-100 transition-colors"
                  [ngClass]="{
                    'bg-gray-50/50': i % 2 === 0,
                    'bg-white': i % 2 !== 0,
                    'opacity-50': !hasCategoryTotals(i),
                  }"
                >
                  <td class="px-4 py-3 font-medium text-gray-700">
                    <span class="inline-flex items-center gap-2">
                      {{ strings.categoryLabel }} {{ i + 1 }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-600">
                    {{ kgToTonnes(categoryTotals[i].co2).toFixed(3) || '–' }}
                  </td>
                  <td class="px-4 py-3 text-right text-gray-600">
                    {{ kgToTonnes(categoryTotals[i].ch4).toFixed(3) || '–' }}
                  </td>
                  <td class="px-4 py-3 text-right text-gray-600">
                    {{ kgToTonnes(categoryTotals[i].n2o).toFixed(3) || '–' }}
                  </td>
                  <td class="px-4 py-3 text-right font-bold text-[#043B22]">
                    {{ kgToTonnes(categoryTotals[i].co2e).toFixed(3) || '–' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Total Emissions Banner -->
        <div class="total-banner mb-4 text-white rounded-xl p-5 mt-6 shadow-lg">
          <div class="relative z-10">
            <div class="flex items-center gap-3 mb-4">
              <div
                class="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  ></path>
                </svg>
              </div>
              <div class="text-lg sm:text-xl futura font-bold">
                {{ strings.totalEmissionsTitle }}
              </div>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div class="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                <div
                  class="text-[10px] uppercase tracking-wider text-white/70 mb-1"
                >
                  CO₂
                </div>
                <div class="text-[14px] sm:text-[16px] font-bold">
                  {{ kgToTonnes(grandTotals.co2).toFixed(3) }} t
                </div>
              </div>
              <div class="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                <div
                  class="text-[10px] uppercase tracking-wider text-white/70 mb-1"
                >
                  CH₄
                </div>
                <div class="text-[14px] sm:text-[16px] font-bold">
                  {{ kgToTonnes(grandTotals.ch4).toFixed(3) }} t
                </div>
              </div>
              <div class="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                <div
                  class="text-[10px] uppercase tracking-wider text-white/70 mb-1"
                >
                  N₂O
                </div>
                <div class="text-[14px] sm:text-[16px] font-bold">
                  {{ kgToTonnes(grandTotals.n2o).toFixed(3) }} t
                </div>
              </div>
              <div
                class="bg-white/20 rounded-lg p-3 backdrop-blur-sm border border-white/30"
              >
                <div
                  class="text-[10px] uppercase tracking-wider text-white/90 mb-1"
                >
                  Total CO₂e
                </div>
                <div class="text-[16px] sm:text-[18px] font-bold">
                  {{ kgToTonnes(grandTotals.co2e).toFixed(3) }} t
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  `,
})
export class CalculationSummary {
  language = inject(LocaleService).getLanguage();
  calculationName: string = '';

  localeService = inject(LocaleService);
  private userService = inject(UserService);
  @Input() categoryCompleted: boolean = false;
  // @Input() currentCategory: number = 0;
  @Input() currentQuestion: number = 0;
  @Input() categories: any[] = [];
  @Input() setQuestion: any;
  @Input() locale: any = {};
  @Input() currentCategory!: number;
  get isInvitedUser(): boolean {
    return (
      localStorage.getItem('isInvitedUser') === 'true' &&
      !this.userService.user()?.subscriptionPlan
    );
  }
  get isSummary(): boolean {
    return this.currentCategory === this.categories.length;
  }
  summaryCategory1: boolean = true;
  summaryCategory2: boolean = true;
  summaryCategory3: boolean = true;
  summaryCategory4: boolean = true;
  summaryCategory5: boolean = true;
  summaryCategory6: boolean = true;
  sumC1Q1: any = [];
  sumC1Q1Incl: any = [];
  sumC1Q2: any = [];
  sumC1Q3: any = [];
  sumC1Q4: any = [];
  sumC1Q5: any = [];
  sumC1Q6: any = {};
  sumC1Q7: any = {};
  sumC1Q8: any = {};
  sumC2Q1: any = []; // Cat 2 Q1 removed per client request (kept for reference)
  sumC2Q1Incl: any = [];
  sumC2Q2: any = [];
  sumC2Q2Incl: any = [];
  sumC2Q3: any = {};
  sumC3Q1: any = {};
  sumC3Q2: any = {};
  sumC3Q3: any = {};
  sumC3Q4: any = {};
  sumC3Q5: any = {};
  sumC4Q1: any = {};
  sumC4Q2: any = {};
  sumC4Q3: any = {};
  sumC4Q4: any = {};
  sumC4Q5: any = {};
  strings: any = {};
  sumC5Q1: any = {};
  sumC5Q2: any = {};
  sumC5Q3: any = {};
  sumC5Q4: any = {};
  sumC6Q1: any = {};
  updateStrings() {
    const isItalian = this.localeService.getLanguage() === 'italian';
    this.strings = isItalian ? this.italianStrings : this.englishStrings;
  }

  englishStrings = {
    categorySummaryTitle: 'Category Emissions Summary',
    categoryLabel: 'Category',
    totalEmissionsTitle: 'Total Emissions (All Categories)',
    vehicle: 'Vehicle',
    vehicle_type: 'Vehicle Type',
    fuel_type: 'Fuel Type',
    kilometers_per_year: 'Kilometers per Year',
    liters_per_year: 'Liters per Year',
    km: 'km',
    liters: 'liters',
    car: 'Car',
    motorcycle: 'Motorcycle',
    truck: 'Truck',
    van: 'Van',
    diesel: 'Diesel',
    gas: 'Gasoline',
    fuel: 'Fuel Oil',
    electric: 'Electric',
    hybrid: 'Hybrid',
    room_nights: 'Room-nights per Trip',
    heading_questionnaire_summary: 'Questionnaire Summary',
    subheading_questionnaire_summary: 'Review your answers before submitting',
    heading_category: 'Category',
    button_edit: 'Edit',
    kg: 'Kg',

    liters_short: 'l',
    system: 'System',
    refills: 'Refills',
    kg_per_recharge: 'Kg / recharge',
    emission: 'Emission',
    tonnes: 'tonnes',
    approach: 'Approach',
    kwh: 'kWh',
    unnamed_group: 'Unnamed group',
    employees: 'Employees',
    transport: 'Transport',
    distance: 'Distance',
    trips: 'Trips',
    working_hours: 'Working Hours',
    point_a: 'Point A',
    point_b: 'Point B',
    no_employee_data: 'No employee data entered',
    remote_workers: 'Remote workers',
    smart_working: 'Smart Working',
    no_remote_worker_data: 'No remote worker data entered',
    data_structure_not_provided: 'Data structure for question not provided',
    no_data_entered: 'No data entered',
    visitor_group: 'Visitor group',
    no_visitor_data: 'No visitor data entered',
    departure: 'Departure',
    destination: 'Destination',
    frequency: 'Frequency',
    reduction_measures: 'Reduction Measures',
    business_trip: 'Business trip',
    no_business_trip_data: 'No business trip data entered',
    purchase_category: 'Purchase category',
    spend_region: 'Spend region',
    amount: 'Amount',
    year: 'Year',
    notes: 'Notes',
    dash: '—',
    item: 'Item',
    method: 'Method',
    uuid: 'UUID',
    transport_type: 'Transport type',
    freight_cost: 'Freight cost',
    period_start: 'Period start',
    reference: 'Reference',
    date: 'Date',
    transport_mode: 'Transport mode',
    weight: 'Weight',
    commodity: 'Commodity',
    refrigerated: 'Refrigerated',
    yes: 'Yes',
    no: 'No',
    employee_group: 'Employee group',
    main_category: 'Main category',
    region: 'Region',
    note: 'Note',
    treatment: 'Treatment',
    quantity: 'Quantity',
    period: 'Period',
    field: 'Field',
    building: 'Building',
    type: 'Type',
    epc_rating: 'EPC Rating',
    country: 'Country',
    floor_area: 'Floor Area',
    asset_type: 'Asset Type',
    category: 'Category',
    subtype: 'Subtype',
    annual_distance: 'Annual Distance',
    sub_metered: 'Sub-metered',
    unit: 'Unit',
    lessor: 'Lessor',
    scope1_emissions: 'Scope 1 Emissions',
    scope2_emissions: 'Scope 2 Emissions',
    leased_quantity: 'Leased Quantity',
    total_quantity: 'Total Quantity',
    occupancy_rate: 'Occupancy Rate',
    product_group: 'Product Group',
    units_sold: 'Units sold',
    product_lifetime: 'Product Lifetime',
    final_lifetime: 'Final Lifetime',
    intermediate_product: 'Intermediate Product',
    label: 'Label',
    amount_sold: 'Amount Sold',
    ef_per_use: 'EF per Use',
    methods: 'Methods',
    fuel_combustion: 'Fuel Combustion',
    lifetime_quantity: 'Lifetime Quantity',
    electricity: 'Electricity',
    refrigerant: 'Refrigerant',
    charge: 'Charge',
    leakage: 'Leakage',
    direct_fuel_use: 'Direct Fuel Use',
    refrigerant_leakage: 'Refrigerant Leakage',
    gas_type: 'Gas Type',
    scenario_based: 'Scenario-based',
    scenario: 'Scenario',
    share: 'Share',
    fuel_qty: 'Fuel Quantity',
    refrigerant_leak: 'Refrigerant Leak',
    ghg: 'GHG',
    calculated_from_intermediate: 'Calculated from intermediate product data',
    selected_method: 'Selected Method',
    leased_buildings: 'Leased Buildings',
    lease_period: 'Lease Period',
    lessee: 'Lessee',
    utilization: 'Utilization',
    leased_other_assets: 'Leased Other Assets',
    entries: 'Entries',
    total_energy: 'Total Energy',
    total_area: 'Total Area',
    leased_area: 'Leased Area',
    energy_entries: 'Energy Entries',
    asset_label: 'Asset Label',
    lessee_entity: 'Lessee Entity',
    reporting_year: 'Reporting Year',
    scope2_basis: 'Scope 2 Basis',
    allocation_metric: 'Allocation Metric',
    utilization_metric: 'Utilization Metric',
    mass_sold: 'Mass Sold',
    eol_treatment_methods: 'End-of-Life Treatment Methods',
    waste_type: 'Waste Type',
    investments: 'Investments',
    asset_class: 'Asset Class',
    investment_value: 'Investment Value',
    ef_override: 'EF Override',
    no_data_available: 'No data available',
    na: 'N/A',
    currency: 'currency',
    description: 'Description',
    activity_value: 'Activity Value',
    unit_type: 'Unit Type',
    factor_value: 'Factor Value',
    custom_value: 'Custom Value',
    source: 'Source',
    justification: 'Justification',
    petrol: 'petrol',
  };

  italianStrings = {
    categorySummaryTitle: 'Riepilogo delle emissioni per categoria',
    categoryLabel: 'Categoria',
    totalEmissionsTitle: 'Emissioni totali (tutte le categorie)',
    vehicle: 'Veicolo',
    vehicle_type: 'Tipo di veicolo',
    fuel_type: 'Tipo di carburante',
    kilometers_per_year: 'Chilometri all’anno',
    liters_per_year: 'Litri all’anno',
    km: 'km',
    liters: 'litri',
    car: 'Automobile',
    motorcycle: 'Motocicletta',
    truck: 'Camion',
    van: 'Furgone',
    diesel: 'Diesel',
    gas: 'Benzina',
    petrol: 'Benzina',
    fuel: 'Olio combustibile',
    electric: 'Elettrico',
    hybrid: 'Ibrido',
    room_nights: 'Pernottamenti per viaggio',
    heading_questionnaire_summary: 'Riepilogo Questionario',
    subheading_questionnaire_summary: 'Rivedi le tue risposte prima di inviare',
    heading_category: 'Categoria',
    button_edit: 'Modifica',
    kg: 'Kg',

    liters_short: 'l',
    system: 'Sistema',
    refills: 'Ricariche',

    kg_per_recharge: 'Kg / ricarica',
    emission: 'Emissione',
    tonnes: 'tonnellate',
    approach: 'Approccio',
    kwh: 'kWh',
    unnamed_group: 'Gruppo senza nome',
    employees: 'Dipendenti',
    transport: 'Trasporto',
    distance: 'Distanza',
    trips: 'Viaggi',
    working_hours: 'Ore lavorative',
    point_a: 'Punto A',
    point_b: 'Punto B',
    no_employee_data: 'Nessun dato dipendente inserito',
    remote_workers: 'Lavoratori remoti',
    smart_working: 'Smart Working',
    no_remote_worker_data: 'Nessun dato lavoratore remoto inserito',
    data_structure_not_provided: 'Struttura dati per domanda non fornita',
    no_data_entered: 'Nessun dato inserito',
    visitor_group: 'Gruppo visitatori',
    no_visitor_data: 'Nessun dato visitatore inserito',
    departure: 'Partenza',
    destination: 'Destinazione',
    frequency: 'Frequenza',
    reduction_measures: 'Misure di riduzione',
    business_trip: "Viaggio d'affari",
    no_business_trip_data: "Nessun dato viaggio d'affari inserito",
    purchase_category: 'Categoria acquisto',
    spend_region: 'Regione spesa',
    amount: 'Importo',
    year: 'Anno',
    notes: 'Note',
    dash: '—',
    item: 'Elemento',
    method: 'Metodo',
    uuid: 'UUID',
    transport_type: 'Tipo di trasporto',
    freight_cost: 'Costo del trasporto',
    period_start: 'Inizio periodo',
    reference: 'Riferimento',
    date: 'Data',
    transport_mode: 'Modalità di trasporto',
    weight: 'Peso',
    commodity: 'Merce',
    refrigerated: 'Refrigerato',
    yes: 'Sì',
    no: 'No',
    employee_group: 'Gruppo dipendenti',
    main_category: 'Categoria principale',
    region: 'Regione',
    note: 'Nota',
    treatment: 'Trattamento',
    quantity: 'Quantità',
    period: 'Periodo',
    field: 'Campo',
    building: 'Edificio',
    type: 'Tipo',
    epc_rating: 'Valutazione EPC',
    country: 'Paese',
    floor_area: 'Superficie',
    asset_type: 'Tipo di asset',
    category: 'Categoria',
    subtype: 'Sottotipo',
    annual_distance: 'Distanza annuale',
    sub_metered: 'Sub-misurato',
    unit: 'Unità',
    lessor: 'Locatore',
    scope1_emissions: 'Emissioni Scope 1',
    scope2_emissions: 'Emissioni Scope 2',
    leased_quantity: 'Quantità locata',
    total_quantity: 'Quantità totale',
    occupancy_rate: 'Tasso di occupazione',
    product_group: 'Gruppo prodotto',
    units_sold: 'Unità vendute',
    product_lifetime: 'Durata prodotto',
    final_lifetime: 'Durata finale',
    intermediate_product: 'Prodotto intermedio',
    label: 'Etichetta',
    amount_sold: 'Quantità venduta',
    ef_per_use: 'EF per uso',
    methods: 'Metodi',
    fuel_combustion: 'Combustione carburante',
    lifetime_quantity: 'Quantità a vita',
    electricity: 'Elettricità',
    refrigerant: 'Refrigerante',
    charge: 'Carica',
    leakage: 'Perdita',
    direct_fuel_use: 'Uso diretto carburante',
    refrigerant_leakage: 'Perdita refrigerante',
    gas_type: 'Tipo gas',
    scenario_based: 'Basato su scenario',
    scenario: 'Scenario',
    share: 'Condivisione',
    fuel_qty: 'Quantità carburante',
    refrigerant_leak: 'Perdita refrigerante',
    ghg: 'GHG',
    calculated_from_intermediate: 'Calcolato da dati prodotto intermedio',
    selected_method: 'Metodo selezionato',
    leased_buildings: 'Edifici locati',
    lease_period: 'Periodo locazione',
    lessee: 'Locatario',
    utilization: 'Utilizzo',
    leased_other_assets: 'Altri asset locati',
    entries: 'Voci',
    total_energy: 'Energia totale',
    total_area: 'Area totale',
    leased_area: 'Area locata',
    energy_entries: 'Voci energia',
    asset_label: 'Etichetta asset',
    lessee_entity: 'Entità locatario',
    reporting_year: 'Anno di riferimento',
    scope2_basis: 'Base Scope 2',
    allocation_metric: 'Metrica allocazione',
    utilization_metric: 'Metrica utilizzo',
    mass_sold: 'Massa venduta',
    eol_treatment_methods: 'Metodi trattamento fine vita',
    waste_type: 'Tipo rifiuto',
    investments: 'Investimenti',
    asset_class: 'Classe asset',
    investment_value: 'Valore investimento',
    ef_override: 'Sovrascrittura EF',
    no_data_available: 'Nessun dato disponibile',
    na: 'N/D',
    currency: 'valuta',
    description: 'Descrizione',
    activity_value: 'Valore attività',
    unit_type: 'Tipo di unità',
    factor_value: 'Valore fattore',
    custom_value: 'Valore personalizzato',
    source: 'Fonte',
    justification: 'Giustificazione',
  };

  ngOnInit() {
    this.updateStrings();
    this.summary();
    this.computeSummaryTotals();
    this.calculationName = localStorage.getItem('calculationName') || '';
  }

  ngOnChanges() {
    this.updateStrings();
    this.summary();
  }

  private calculateCategoryTotals(category: any): EmissionTotals {
    const totals: EmissionTotals = {
      co2: 0,
      ch4: 0,
      n2o: 0,
      co2e: 0,
    };

    category.questions?.forEach((q: any) => {
      // Skip hidden questions (e.g. Cat 2 Q1 removed per client request)
      if (q.hidden) return;
      // Handle plain number emissions (e.g. lessor-specific method)
      if (typeof q.emissions === 'number') {
        totals.co2e += q.emissions;
        return;
      }
      if (!Array.isArray(q.emissions)) return;

      q.emissions.forEach((em: any) => {
        // Handle plain number elements within the array (e.g. Cat 6 custom factors)
        if (typeof em === 'number') {
          totals.co2e += em;
          return;
        }
        // Spend API nests data inside .estimate
        const data = em.estimate || em;
        const g = data.constituent_gases;

        if (g) {
          const co2 = Number(g.co2) || 0;
          const ch4 = Number(g.ch4) || 0;
          const n2o = Number(g.n2o) || 0;
          totals.co2 += co2;
          totals.ch4 += ch4;
          totals.n2o += n2o;
          // co2e_total and data.co2e are GWP-weighted; gasSum is raw kg (not CO2e)
          // Priority: co2e_total (explicit) → data.co2e (top-level) → gasSum (last resort)
          const gasSum = co2 + ch4 + n2o;
          totals.co2e +=
            g.co2e_total != null
              ? Number(g.co2e_total)
              : Number(data.co2e) || (gasSum > 0 ? gasSum : 0);
        } else if (data.co2e != null) {
          // Travel API: no constituent_gases, only top-level co2e
          totals.co2e += Number(data.co2e) || 0;
        }
      });
    });
    localStorage.setItem(
      CATEGORY_TOTALS_KEY,
      JSON.stringify(this.categoryTotals),
    );
    return totals;
  }

  categoryTotals: Record<number, EmissionTotals> = {};
  grandTotals: EmissionTotals = {
    co2: 0,
    ch4: 0,
    n2o: 0,
    co2e: 0,
  };

  computeSummaryTotals() {
    this.categoryTotals = {};
    this.grandTotals = { co2: 0, ch4: 0, n2o: 0, co2e: 0 };

    this.categories.forEach((category: any, index: number) => {
      if (!category?.questions) return;

      const totals = this.calculateCategoryTotals(category);
      this.categoryTotals[index] = totals;

      this.grandTotals.co2 += totals.co2;
      this.grandTotals.ch4 += totals.ch4;
      this.grandTotals.n2o += totals.n2o;
      this.grandTotals.co2e += totals.co2e;
    });
  }

  kgToTonnes(value?: number): number {
    return (value ?? 0) / 1000;
  }

  hasCategoryTotals(index: number): boolean {
    const t = this.categoryTotals[index];
    return !!t && t.co2e > 0;
  }

  summary = () => {
    this.sumC1Q1 = [];
    this.sumC1Q1Incl = [];
    this.sumC1Q2 = [];
    this.sumC1Q3 = [];
    this.sumC1Q4 = [];
    this.sumC1Q5 = [];
    this.sumC1Q6 = {};
    this.sumC1Q7 = {};
    this.sumC1Q8 = {};
    this.sumC2Q1 = [];
    this.sumC2Q1Incl = [];
    this.sumC2Q2 = [];
    this.sumC2Q2Incl = [];
    this.sumC2Q3 = {};
    this.sumC3Q1 = {};
    this.sumC3Q2 = {};
    this.sumC3Q3 = {};
    this.sumC3Q4 = {};
    this.sumC3Q5 = {};
    this.sumC4Q1 = {};
    this.sumC4Q2 = {};
    this.sumC4Q3 = {};
    this.sumC4Q4 = {};
    this.sumC4Q5 = {};
    this.sumC5Q1 = {};
    this.sumC5Q2 = {};
    this.sumC5Q3 = {};
    this.sumC5Q4 = {};
    this.sumC6Q1 = {};

    let answers = this.categories[0]?.questions?.[0].answers;

    Object.keys(answers ?? {}).forEach((key) => {
      if (
        key == 'ann' &&
        answers[key].length > 0 &&
        !this.sumC1Q1.includes(key)
      ) {
        this.sumC1Q1.push({
          month: this.locale.common.annual,
          value: answers[key],
        });
        this.sumC1Q1Incl.push(key);
      } else if (answers['ann'].length < 1) {
        if (key !== 'ann' && !this.sumC1Q1Incl.includes(key)) {
          this.sumC1Q1.push({
            month: this.monthMapper(key),
            value: answers[key],
          });
          this.sumC1Q1Incl.push(key);
        }
      }
    });
    this.sumC1Q2 = Object.values(
      this.categories[0]?.questions?.[1].answers || {},
    );
    this.sumC1Q3 = Object.values(
      this.categories[0]?.questions?.[2]?.answers || {},
    );
    this.sumC1Q4 = Object.values(
      this.categories[0]?.questions?.[3]?.answers || {},
    );
    this.sumC1Q5 = Object.values(
      this.categories[0]?.questions?.[4]?.answers || {},
    );
    this.sumC1Q6 = Object.values(
      this.categories[0]?.questions?.[5]?.answers || {},
    );
    this.sumC1Q7 = Object.values(
      this.categories[0]?.questions?.[6]?.answers || {},
    );
    this.sumC1Q8 = Object.values(
      this.categories[0]?.questions?.[7]?.answers || {},
    );

    // Cat 2 Q1 removed per client request
    // answers = this.categories[1]?.questions?.[0].answers;
    // Object.keys(answers ?? {}).forEach(key => {
    //   if (key == 'ann' && answers[key].length > 0 && !this.sumC2Q1Incl.includes(key)) {
    //     this.sumC2Q1.push({ month: this.locale.common.annual, value: answers[key] });
    //     this.sumC2Q1Incl.push(key);
    //   } else if (answers['ann'].length < 1) {
    //     if (key !== 'ann' && !this.sumC2Q1Incl.includes(key)) {
    //       this.sumC2Q1.push({ month: this.monthMapper(key), value: answers[key] });
    //       this.sumC2Q1Incl.push(key);
    //     }
    //   }
    // });

    answers = this.categories[1]?.questions?.[1].answers;

    if (Array.isArray(answers)) {
      // New multi-entry array format: [{electricitySource, ann}, ...]
      for (const entry of answers) {
        if (entry.ann) {
          this.sumC2Q2.push({
            month: this.locale.common.annual,
            value: String(entry.ann),
            electricitySource: entry.electricitySource,
            showUnit: true,
          });
        }
      }
    } else {
      // Legacy single-object format: {electricitySource, ann} or {electricitySource, jan, feb, ...}
      Object.keys(answers ?? {}).forEach((key) => {
        if (key === 'electricitySource' && !this.sumC2Q2Incl.includes(key)) {
          this.sumC2Q2.push({
            month: this.strings.electricity_source,
            electricitySource: answers[key],
            showUnit: false,
          });
        } else if (
          key === 'ann' &&
          answers[key]?.length > 0 &&
          !this.sumC2Q2Incl.includes(key)
        ) {
          this.sumC2Q2.push({
            month: this.locale.common.annual,
            value: answers[key],
            showUnit: true,
          });
          this.sumC2Q2Incl.push(key);
        } else if (answers['ann']?.length < 1) {
          if (
            key !== 'ann' &&
            key !== 'electricitySource' &&
            !this.sumC2Q2Incl.includes(key)
          ) {
            this.sumC2Q2.push({
              month: this.monthMapper(key),
              value: answers[key],
              showUnit: true,
            });
            this.sumC2Q2Incl.push(key);
          }
        }
      });
    }

    const answerssumC2Q3 = this.categories[1]?.questions?.[2]?.answers;
    if (answerssumC2Q3) {
      const values = Object.values(answerssumC2Q3);
      this.sumC2Q3 = Array.isArray(values[1]) ? values[1] : [];
    } else {
      this.sumC2Q3 = [];
    }

    // Category 3
    const valuessumC3Q1 = Object.values(
      this.categories[2]?.questions?.[0]?.answers || {},
    );
    this.sumC3Q1 = {
      method: valuessumC3Q1[2],
      spendData: valuessumC3Q1[3],
      activityData: valuessumC3Q1[4],
    };

    const valuessumC3Q2 = Object.values(
      this.categories[2]?.questions?.[1]?.answers || {},
    );
    this.sumC3Q2 = {
      method: valuessumC3Q2[2],
      spendData: valuessumC3Q2[3],
      activityData: valuessumC3Q2[4],
    };

    this.sumC3Q3 = Object.values(
      this.categories[2]?.questions?.[2]?.answers || {},
    );
    this.sumC3Q4 = Object.values(
      this.categories[2]?.questions?.[3]?.answers || {},
    );
    this.sumC3Q5 = Object.values(
      this.categories[2]?.questions?.[4]?.answers || {},
    );

    // Category 4
    this.sumC4Q1 = Object.values(
      this.categories[3]?.questions?.[0]?.answers || {},
    );
    this.sumC4Q2 = Object.values(
      this.categories[3]?.questions?.[1]?.answers || {},
    );
    this.sumC4Q3 = Object.values(
      this.categories[3]?.questions?.[2]?.answers || {},
    );
    this.sumC4Q4 = this.categories[3]?.questions?.[3]?.answers;
    this.sumC4Q5 = Object.values(
      this.categories[3]?.questions?.[4]?.answers || {},
    );

    // Category 5
    this.sumC5Q1 = this.categories[4]?.questions?.[0]?.answers;
    this.sumC5Q2 = this.categories[4]?.questions?.[1]?.answers;
    this.sumC5Q3 = this.categories[4]?.questions?.[2]?.answers;
    this.sumC5Q4 = this.categories[4]?.questions?.[3]?.answers;

    // Category 6
    this.sumC6Q1 = Object.values(
      this.categories[5]?.questions?.[0]?.answers || {},
    );
  };
  transportModes = [
    { value: 'flight', label: 'Flight', labelIt: 'Aereo' },
    { value: 'train', label: 'Train', labelIt: 'Treno' },
    { value: 'car', label: 'Car', labelIt: 'Macchina' },
  ];

  getTransportLabel(value: string): string {
    const mode = this.transportModes.find((m) => m.value === value);
    if (!mode) return value;
    return this.language === 'italian' ? mode.labelIt : mode.label;
  }

  monthMapper = (key: any) => {
    if (key === 'jan') {
      return this.locale.common.jan;
    }
    if (key === 'feb') {
      return this.locale.common.feb;
    }
    if (key === 'mar') {
      return this.locale.common.mar;
    }
    if (key === 'apr') {
      return this.locale.common.apr;
    }
    if (key === 'may') {
      return this.locale.common.may;
    }
    if (key === 'jun') {
      return this.locale.common.jun;
    }
    if (key === 'jul') {
      return this.locale.common.jul;
    }
    if (key === 'aug') {
      return this.locale.common.aug;
    }
    if (key === 'sep') {
      return this.locale.common.sep;
    }
    if (key === 'oct') {
      return this.locale.common.oct;
    }
    if (key === 'nov') {
      return this.locale.common.nov;
    }
    if (key === 'dec') {
      return this.locale.common.dec;
    }
    return key;
  };

  getSumC4Answer(index: number) {
    const mapping = [
      this.sumC4Q1,
      this.sumC4Q2,
      this.sumC4Q3,
      this.sumC4Q4,
      this.sumC4Q5,
    ];
    return mapping[index];
  }
  formatAssetClass(value: string): string {
    if (!value) return value;
    return value
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/type/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  assetClassMap: { [key: string]: string } = {
    'financial_services-type_financial_intermediation_except_insurance_and_pension_funding-basis_industry':
      'Project finance',
    'financial_services-type_financial_intermediation_services_except_insurance_pension_funding_services':
      'Listed equity & corporate bonds',
    'financial_services-type_services_auxiliary_to_financial_intermediation':
      'Corporate loans',
  };

  formatAssetClass2(value: string): string {
    return this.assetClassMap[value] || value; // fallback to raw value if not found
  }
  hasValidData(data: any[]): boolean {
    if (!Array.isArray(data) || data.length === 0) return false;

    return data.some((item) => {
      return (
        item.description ||
        item.activity_value ||
        item.factor_value ||
        item.custom_value ||
        item.factor_source ||
        item.justification
      );
    });
  }
  getLabels(key: string): string {
    return this.strings[key] || key;
  }
  getLabel(list: any[], value: string): string {
    const item = list?.find((i) => i.value === value);
    return item ? item.label : value || this.strings.na;
  }
  getVehicleSubtypeLabel(category: string, subtypeValue: string): string {
    return this.getLabel(this.vehicleSubtypes[category] || [], subtypeValue);
  }
  vehicleSubtypes: Record<string, any[]> = {
    Vehicles_and_trucks: [
      {
        value: 'passenger-car-small',
        label:
          this.language === 'english'
            ? 'Passenger car – small'
            : 'Auto passeggeri – piccola',
      },
      {
        value: 'passenger-car-medium',
        label:
          this.language === 'english'
            ? 'Passenger car – medium'
            : 'Auto passeggeri – media',
      },
      {
        value: 'light-van',
        label: this.language === 'english' ? 'Light van' : 'Furgone leggero',
      },
      {
        value: 'rigid-truck',
        label: this.language === 'english' ? 'Rigid truck' : 'Camion rigido',
      },
      {
        value: 'articulated-truck',
        label:
          this.language === 'english'
            ? 'Articulated truck'
            : 'Camion articolato',
      },
      {
        value: 'electric-car',
        label: this.language === 'english' ? 'Electric car' : 'Auto elettrica',
      },
      {
        value: 'plug-in-hybrid-car',
        label:
          this.language === 'english'
            ? 'Plug-in hybrid car'
            : 'Auto ibrida plug-in',
      },
      {
        value: 'motorcycle-scooter',
        label:
          this.language === 'english'
            ? 'Motorcycle / scooter'
            : 'Moto / scooter',
      },
    ],
  };
  leasedAssetCategories = [
    {
      value: 'vehicle',
      label: this.language === 'english' ? 'Vehicle' : 'Veicolo',
    },
    {
      value: 'mobile-machinery',
      label:
        this.language === 'english' ? 'Mobile machinery' : 'Macchinario mobile',
    },
    {
      value: 'office-equipment',
      label:
        this.language === 'english'
          ? 'Office equipment'
          : 'Attrezzatura per ufficio',
    },
    {
      value: 'it-equipment',
      label: this.language === 'english' ? 'IT equipment' : 'Attrezzatura IT',
    },
    {
      value: 'other',
      label: this.language === 'english' ? 'Other' : 'Altro',
    },
  ];
  epcRatings = [
    {
      value: 'a',
      label: this.language === 'english' ? 'A (Outstanding)' : 'A (Eccellente)',
    },
    {
      value: 'b',
      label: this.language === 'english' ? 'B (Excellent)' : 'B (Ottimo)',
    },
    {
      value: 'c',
      label: this.language === 'english' ? 'C (Good)' : 'C (Buono)',
    },
    {
      value: 'd',
      label: this.language === 'english' ? 'D (Average)' : 'D (Medio)',
    },
    {
      value: 'e',
      label: this.language === 'english' ? 'E (Poor)' : 'E (Scarso)',
    },
    {
      value: 'f',
      label: this.language === 'english' ? 'F (Very Poor)' : 'F (Molto scarso)',
    },
    {
      value: 'not-provided',
      label: this.language === 'english' ? 'Not Provided' : 'Non fornito',
    },
  ];
  buildingTypes = [
    {
      value: 'office',
      label: this.language === 'english' ? 'Office' : 'Ufficio',
      activityId: 'real_estate-type_office',
      unit: 'm2',
    },
    {
      value: 'distribution-warehouse-warm',
      label:
        this.language === 'english'
          ? 'Distribution Warehouse - Warm'
          : 'Magazzino di distribuzione (non refrigerato)',
      activityId: 'real_estate-type_distribution_warehouse_warm',
      unit: 'm2',
    },
    {
      value: 'distribution-warehouse-cold',
      label:
        this.language === 'english'
          ? 'Distribution Warehouse - Cold'
          : 'Magazzino di distribuzione frigorifero',
      activityId: 'real_estate-type_distribution_warehouse_cold',
      unit: 'm2',
    },
    {
      value: 'retail-high-street',
      label:
        this.language === 'english'
          ? 'Retail - High Street'
          : 'Negozio al dettaglio - strada principale',
      activityId: 'real_estate-type_retail_high_street',
      unit: 'm2',
    },
    {
      value: 'retail-warehouse',
      label:
        this.language === 'english'
          ? 'Retail - Warehouse'
          : 'Negozio al dettaglio - magazzino',
      activityId: 'real_estate-type_retail_warehouse',
      unit: 'm2',
    },
    {
      value: 'retail-shopping-centre',
      label:
        this.language === 'english'
          ? 'Retail - Shopping Centre'
          : 'Negozio al dettaglio - centro commerciale',
      activityId: 'real_estate-type_retail_shopping_center',
      unit: 'm2',
    },
    {
      value: 'hotel',
      label: this.language === 'english' ? 'Hotel' : 'Hotel / Albergo',
      activityId: 'real_estate-type_hotel',
      unit: 'm2',
    },
    {
      value: 'healthcare',
      label:
        this.language === 'english'
          ? 'Healthcare'
          : 'Sanità / Assistenza sanitaria',
      activityId: 'real_estate-type_healthcare',
      unit: 'm2',
    },
    {
      value: 'leisure-lodging',
      label:
        this.language === 'english'
          ? 'Leisure/Lodging'
          : 'Tempo libero o alloggio',
      activityId: 'real_estate-type_leisure_lodging',
      unit: 'm2',
    },
    {
      value: 'non_residential',
      label:
        this.language === 'english'
          ? 'Non-residential (generic)'
          : 'Edificio non residenziale',
      activityId: 'real_estate-type_non_residential_total',
      unit: 'm2',
    },
  ];
  utilizationMetrics = [
    {
      value: 'km',
      label: this.language === 'english' ? 'Kilometers' : 'Chilometri',
    },
    {
      value: 'engine-hours',
      label: this.language === 'english' ? 'Engine hours' : 'Ore motore',
    },
    {
      value: 'operating-hours',
      label: this.language === 'english' ? 'Operating hours' : 'Ore operative',
    },
  ];
  scope2BasisOptions = [
    {
      value: 'market-based',
      label:
        this.language === 'english' ? 'Market-based' : 'Basato sul mercato',
    },
    {
      value: 'location-based',
      label:
        this.language === 'english'
          ? 'Location-based'
          : 'Basato sulla posizione',
    },
  ];
  fuelUnits = [
    { value: 'kWh', label: 'kWh' },
    { value: 'm³', label: 'm³' },
    {
      value: 'liters',
      label: this.language === 'english' ? 'Liters' : 'Litri',
    },
    {
      value: 'unit',
      label: this.language === 'english' ? 'Unit' : 'unità',
    },
    { value: 'kg', label: 'kg' },
  ];
  fuelTypes = [
    {
      value: 'electricity-supply_grid-source_total_supplier_mix',
      label: this.language === 'english' ? 'Electricity' : 'Elettricità',
      unit: 'kWh', // Default unit for electricity
    },
    {
      value: 'fuel-type_natural_gas-fuel_use_na',
      label: this.language === 'english' ? 'Natural Gas' : 'Gas naturale',
      unit: 'm³', // Default unit for natural gas
    },
    {
      value: 'fuel-type_diesel-fuel_use_na',
      label: this.language === 'english' ? 'Diesel' : 'Gasolio',
      unit: 'liters', // Default unit for diesel
    },
    {
      value: 'fuel-type_heating_oil-fuel_use_stationary',
      label: this.language === 'english' ? 'Heating oil' : 'Heating oil',
      unit: 'liters', // Default unit for gasoline
    },
  ];
  vehicleCategories = [
    {
      value: 'Vehicles_and_trucks',
      label:
        this.language === 'english'
          ? 'Vehicles and trucks'
          : 'Veicoli e camion',
    },
  ];
  unitTypeOptions: { [key: string]: any[] } = {
    weight: [
      { value: 'g', label: 'g', labelIt: 'g' },
      { value: 'kg', label: 'kg', labelIt: 'kg' },
      { value: 't', label: 't (tonne)', labelIt: 't (tonnellata)' },
      { value: 'ton', label: 'ton (US)', labelIt: 'ton (USA)' },
      { value: 'lb', label: 'lb (pound)', labelIt: 'lb (libbra)' },
    ],

    volume: [
      { value: 'ml', label: 'ml', labelIt: 'ml' },
      { value: 'l', label: 'l', labelIt: 'l' },
      { value: 'm3', label: 'm³ (cubic metre)', labelIt: 'm³ (metro cubo)' },
      {
        value: 'cubic_foot',
        label: 'ft³ (cubic foot)',
        labelIt: 'ft³ (piede cubo)',
      },
      {
        value: 'gallon_us',
        label: 'gal (US gallon)',
        labelIt: 'gal (gallone USA)',
      },
      { value: 'bbl', label: 'bbl (barrel)', labelIt: 'bbl (barile)' },
    ],

    distance: [
      { value: 'm', label: 'm', labelIt: 'm' },
      { value: 'km', label: 'km', labelIt: 'km' },
      { value: 'mi', label: 'mi (mile)', labelIt: 'mi (miglio)' },
      { value: 'ft', label: 'ft (foot)', labelIt: 'ft (piede)' },
    ],

    money: [
      {
        value: 'usd',
        label: 'USD (US Dollar)',
        labelIt: 'USD (Dollaro statunitense)',
      },
      { value: 'eur', label: 'EUR (Euro)', labelIt: 'EUR (Euro)' },
      {
        value: 'gbp',
        label: 'GBP (Pound Sterling)',
        labelIt: 'GBP (Sterlina britannica)',
      },
      {
        value: 'cad',
        label: 'CAD (Canadian Dollar)',
        labelIt: 'CAD (Dollaro canadese)',
      },
      {
        value: 'aud',
        label: 'AUD (Australian Dollar)',
        labelIt: 'AUD (Dollaro australiano)',
      },
      {
        value: 'chf',
        label: 'CHF (Swiss Franc)',
        labelIt: 'CHF (Franco svizzero)',
      },
      {
        value: 'sek',
        label: 'SEK (Swedish Krona)',
        labelIt: 'SEK (Corona svedese)',
      },
      {
        value: 'nok',
        label: 'NOK (Norwegian Krone)',
        labelIt: 'NOK (Corona norvegese)',
      },
      {
        value: 'dkk',
        label: 'DKK (Danish Krone)',
        labelIt: 'DKK (Corona danese)',
      },
      {
        value: 'jpy',
        label: 'JPY (Japanese Yen)',
        labelIt: 'JPY (Yen giapponese)',
      },
      {
        value: 'cny',
        label: 'CNY (Chinese Yuan)',
        labelIt: 'CNY (Yuan cinese)',
      },
    ],

    number: [
      { value: 'item', label: 'Item', labelIt: 'Elemento' },
      { value: 'money', label: 'Money', labelIt: 'Soldi' },
      { value: 'number', label: 'Number', labelIt: 'Numero' },
    ],
  };
  unitTypeLabels: Record<string, { label: string; labelIt: string }> = {
    weight: { label: 'Weight', labelIt: 'Peso' },
    volume: { label: 'Volume', labelIt: 'Volume' },
    distance: { label: 'Distance', labelIt: 'Distanza' },
    money: { label: 'Money', labelIt: 'Valuta' },
    number: { label: 'Number', labelIt: 'Numero' },
  };

  electricitySourceOptions = [
    {
      value: 'grid',
      label: 'Purchased from the National Grid',
      italianLabel: 'Acquistata dalla rete nazionale',
    },
    {
      value: 'renewable_supplier',
      label: 'Purchased from Renewable Supplier (PPA or Green Tariff)',
      italianLabel:
        'Acquistata tramite fornitore rinnovabile (PPA o tariffa verde)',
    },
    {
      value: 'self_generated',
      label: 'Self-generated On-Site (Solar, Wind, CHP, etc.)',
      italianLabel:
        'Autoprodotta da impianto on-site (solare, eolico, cogenerazione)',
    },
    {
      value: 'imported_other',
      label: 'Imported from Another Facility or Country',
      italianLabel: 'Importata da altra sede o paese',
    },
  ];

  getElectricitySourceLabel(value: string): string {
    if (!value) return '';

    const source = this.electricitySourceOptions.find(
      (opt) => opt.value === value,
    );

    if (source) {
      return this.language === 'italian' ? source.italianLabel : source.label;
    }

    return value;
  }

  getUnitLabel(value: string): string {
    if (!value) return '';
    const typeLabel = this.unitTypeLabels[value];
    if (typeLabel) {
      return this.language === 'italian' ? typeLabel.labelIt : typeLabel.label;
    }
    for (const key in this.unitTypeOptions) {
      const unit = this.unitTypeOptions[key].find((u) => u.value === value);

      if (unit) {
        return this.language === 'italian' ? unit.labelIt : unit.label;
      }
    }
    return value;
  }

  getActivityUnitLabel(unitValue: string): string {
    for (const key in this.unitTypeOptions) {
      const unit = this.unitTypeOptions[key].find((u) => u.value === unitValue);

      if (unit) {
        return this.language === 'italian' ? unit.labelIt : unit.label;
      }
    }

    return unitValue; // fallback
  }

  getAnswerRow(label: string, value: any) {
    const str = value == null ? '' : String(value).trim();
    let clean = str;
    // "null", "null tonnes", "undefined km" etc.
    if (/^(null|undefined)(\s.*)?$/i.test(str)) {
      clean = '';
    }
    // Bare unit with no numeric value e.g. "Kg", "tonnes", "km"
    else if (
      /^(km|kg|kwh|tonnes|tonnellate|liters|litri|co2e|tco2e|l|t)\s*$/i.test(
        str,
      )
    ) {
      clean = '';
    }
    return `<p class="text-[12px] grid grid-cols-2 gap-3 pb-1">
    <span class="text-[#616161]">${label}:</span> <span class="">${clean}</span>
  </p>`;
  }
}
